import { createContext, useContext, useState } from "react";
import "./PatternsPage.css";
import "./SettingsPage.css";
import { useTokenValues } from "../lib/tokens";
import { AppShell, SideAction, SideCheck, SideGroup, SideLink, SideNote, SideRail } from "../app/AppShell";
import { IssueCard } from "../components/IssueCard";
import { IssueActions } from "../components/IssueActions";
import { ChildrenPanel, DepsEditor, IssueQuestions, IssueSpecEditor, SupplementCard } from "../components/IssueEdit";
import { BoardSelBar } from "../components/BoardSelBar";
import type { IssueActsApi, ProgressRow } from "../lib/tasks";
import { IssueTabs, type IssueTab } from "../components/IssueTabs";
import { CopyLinkButton, MetaLine } from "../components/IssueDetail";
import { ISSUES, MILESTONES, MOCK_NOW } from "../mock/issues";
import { Button } from "../components/Button";
import type { ButtonVariant, CtlSize } from "../components/Button";
import { Alert, Card, EmptyState, Loading } from "../components/Card";
import { FormSplit, PageHead, StatCard } from "../components/Page";
import { UsageStates } from "./PatternUsage";
import { DashStates } from "./PatternDash";
import { MilestoneStates } from "./PatternMilestones";
import { ProjectStates } from "./PatternProjects";
import { OpsStates } from "./PatternOps";
import { HistoryStates } from "./PatternHistory";
import { QueueStates } from "./PatternQueue";
import { GraphStates } from "./PatternGraph";
import { RepoFilterStates } from "./PatternRepos";
import { Field, Input, Select } from "../components/Field";
import { Icon, ICON_NAMES } from "../components/Icon";
import { Spinner } from "../components/Spinner";
import { Chip, STATUS_KEYS, StatusPill } from "../components/Status";
import { StageStrip } from "../components/StageStrip";
import type { StageCell } from "../components/StageStrip";
import { FOOTPRINT_TAB, SessionTabs } from "../components/SessionTabs";
import { FootprintRow, HumanRow, LogRow, SessionNote } from "../components/ChatRows";
import { ChatInput } from "../components/ChatInput";
import { BANNER as GIT_BANNER, TEST_KINDS, usageText } from "./GitPage";
import { BANNER as CM_BANNER, TEST_KINDS as CM_TEST_KINDS } from "./CodePage";
import { ChoreForm, ChoreList, ChoreRow } from "../components/Chores";
import { PAT_CHORES, PAT_CHORE_NOW } from "../mock/chores";
import { RepoCreateForm } from "../components/RepoCreate";
import { PAT_ORGS, PAT_ORG_CONNS, PAT_ORG_MEMBERS, PAT_ORG_PROJECTS } from "../mock/orgs";
import { AddMemberForm, MemberTable, NewOrgForm, OrgCard, OrgGrid, OrgManage, RoleGuide } from "../components/Orgs";
import { PAT_MCP_NOW, PAT_MCP_PROJECTS, PAT_MCP_TOKENS } from "../mock/mcpTokens";
import { McpTokenCard, McpTokenEdit, McpTokenTable, NewMcpTokenForm } from "../components/McpTokens";
import { GLYPH as LOG_GLYPH, LEVELS as LOG_LEVELS, STATE_TEXT as LOG_STATE } from "./LogsPage";
import type { ReadState } from "./LogsPage";
import { TRACKS } from "../chat/api";
import type { LogEntry, Message, Session, TaskEvent } from "../chat/api";
import { KindChip, ProposalStatusChip, TidyLegend, TidyResult, TidyScope, TidyTable } from "../components/Tidy";
import type { TidyRow } from "../components/Tidy";
import { TidyBody } from "./TidyPage";
import { AlarmItem, AlarmKindChip, AlarmListState, AlarmTable, BellButton, BellPanel } from "../components/Alarm";
import { PwaNoticeSample } from "../components/Pwa";
import { PatternStates } from "./PatternStates";
import { GitConnSection } from "../components/GitConn";
import { RepoChecksChip, RepoChecksEditor } from "../components/RepoChecks";
import type { Checks } from "../components/RepoChecks";
import { OAuthAskCard, OAuthEndCard } from "../components/OAuthCard";
import type { GitConnList, OAuthRequest } from "../lib/gitconn";
import { NewRunnerForm, PairCodeForm, PairEndCard, PairRequestCard, RunnerTable, RunnerTokenCard } from "../components/Runner";
import type { RunnerPairRequest, RunnerRow } from "../lib/auth";
import { AddRepoForm, AppendList, ServerMark, ServiceTable } from "../components/Servers";
import type { Server } from "../lib/servers";
import type { Issue } from "../lib/tasks";
import { AccountInfo, AccountMenu, AccountPanel, LoginCard, NewUserForm, PasswordCard, SetupCard, TokenWhereCard, UserTable } from "../components/Auth";
import type { Me, UserRow } from "../lib/auth";
import { TotpEnrollGate, TotpLoginCard } from "@mops/totp-ui-v2";
import { TotpAccountCard, TotpAdminResetCell, TotpPolicyCard } from "../components/Totp";
import type { Alarm } from "../lib/alarms";
import { KIND_KEYS } from "../lib/tidy";
import type { Proposal } from "../lib/tidy";
import { hole, rich, tx } from "../i18n";

/* ══ 디자인 패턴 페이지 ══
   **만든 컴포넌트는 전부 여기 올린다**(AGENTS.md 규칙). 숨긴 개발자 페이지가 아니라
   내비바에 상시 서 있는 검토 자리다 — 보면서 계속 고친다.

   여기 적힌 값은 전부 토큰 이름으로 읽어 그린다. 표에 값을 손으로 베껴 적으면
   토큰을 고쳤을 때 이 페이지만 옛날 값을 말하게 된다. */

// 램프 이름 — 값은 공통 v3 tokens.css 에서 읽어 그린다(손으로 베껴 적지 않는다).
const TYPE_ROWS = [
  { tag: "h1", token: "--t-h1", sample: tx("pat.type.h1") },
  { tag: "h2", token: "--t-h2", sample: tx("pat.type.h2") },
  { tag: "h3", token: "--t-h3", sample: tx("pat.type.h3") },
  { tag: "h4", token: "--t-h4", sample: tx("pat.type.h4") },
  { tag: "h5", token: "--t-h5", sample: tx("pat.type.h5") },
  { tag: "h6", token: "--t-h6", sample: tx("pat.type.h6") },
  { tag: "body", token: "--t-body", sample: tx("pat.type.body") },
  { tag: "caption", token: "--t-caption", sample: tx("pat.type.caption") },
  { tag: "code", token: "--t-code", sample: "b3x-mcp reserve_tasks(...)" },
  { tag: "label", token: "--t-label", sample: tx("pat.type.label") },
  { tag: "eyebrow", token: "--t-eyebrow", sample: tx("pat.type.eyebrow") },
] as const;

// 굵기는 램프가 정한다 — 본문 400 · 라벨 500 · 제목·eyebrow 600. 상한은 600 이다(700 은 쓰지 않는다).
const WEIGHTS = [
  { weight: 400, use: tx("pat.weight.w400") },
  { weight: 500, use: tx("pat.weight.w500") },
  { weight: 600, use: tx("pat.weight.w600") },
] as const;

// 표면 사다리 5단 + 테두리 3단 + 비활성 테두리 — 공통 v3 이름(값은 Base UI).
const SURFACES = ["--canvas", "--surface-1", "--surface-2", "--surface-3", "--surface-4"];
const BORDERS = ["--hairline", "--hairline-strong", "--border-control", "--hairline-tertiary"];
const FOREGROUNDS = ["--ink", "--ink-muted", "--ink-subtle", "--ink-tertiary"];
const ACCENTS = ["--primary-text", "--primary", "--primary-hover", "--primary-pressed", "--primary-subtle", "--focus-ring"];
const STATUS_TOKENS = ["success", "warning", "danger", "info"];
const SPACING = ["--space-1", "--space-2", "--space-3", "--space-4", "--space-5", "--space-6", "--space-8", "--space-10", "--space-12", "--space-16"];
const RADII = ["--radius-xs", "--radius-sm", "--radius-md", "--radius-lg", "--radius-full"];
// 화면 CSS 가 쓰는 것은 원시값이 아니라 이 자리 이름이다.
const RADIUS_ROLES = ["--radius-ctl", "--radius-ctl-sm", "--radius-card", "--radius-float", "--radius-badge"];
// 그림자는 둘만 쓴다 — 붙은 판의 윗변(edge)과 떠 있는 층(float).
const SHADOWS = ["--shadow-edge", "--shadow-float"];
const ICON_SIZES = ["--icon-sm", "--icon-md", "--icon-lg"];

const CTL_SIZES: { size: CtlSize; where: string }[] = [
  { size: "sm", where: tx("pat.spec.sm") },
  { size: "md", where: tx("pat.spec.md") },
  { size: "lg", where: tx("pat.spec.lg") },
];

const VARIANTS: ButtonVariant[] = ["primary", "secondary", "ghost", "danger"];

/* ── 채팅 겉창의 부품 — 고정 자료로 상태별로 세운다(t-in-chat-drawer, 시안 안 B) ──
   실제 API 모양(`chat/api.ts`)과 같은 타입이라, 모양이 바뀌면 여기가 먼저 안 맞는다. */
const CHAT_NOW = Date.parse("2026-09-13T12:00:00Z");
const chatAgo = (min: number) => new Date(CHAT_NOW - min * 60_000).toISOString();

const CHAT_STAGES: StageCell[] = [
  { key: "plan", label: tx("chat.track.plan"), count: 1, running: false, next: false, lastOutcome: "ok" },
  { key: "design", label: tx("chat.track.design"), count: 0, running: false, next: false, lastOutcome: null },
  { key: "run", label: tx("chat.track.run"), count: 3, running: true, next: false, lastOutcome: null },
  { key: "review", label: tx("chat.track.review"), count: 0, running: false, next: true, lastOutcome: null },
  { key: "merge", label: tx("chat.track.merge"), count: 0, running: false, next: false, lastOutcome: null },
];
const CHAT_STAGES_FAILED: StageCell[] = [
  { key: "plan", label: tx("chat.track.plan"), count: 1, running: false, next: false, lastOutcome: "ok" },
  { key: "design", label: tx("chat.track.design"), count: 0, running: false, next: false, lastOutcome: null },
  { key: "run", label: tx("chat.track.run"), count: 2, running: false, next: false, lastOutcome: "auth" },
  { key: "review", label: tx("chat.track.review"), count: 0, running: false, next: false, lastOutcome: null },
  { key: "merge", label: tx("chat.track.merge"), count: 0, running: false, next: false, lastOutcome: null },
];

function chatSession(p: Partial<Session> & Pick<Session, "id">): Session {
  return {
    taskId: "t-in-chat-mailbox",
    taskTitle: "우편함 — 세션에 말을 건다", // copy-ignore: 견본 자료
    runner: "naru-1",
    track: "run",
    vendor: "claude",
    outcome: null,
    note: null,
    startedAt: chatAgo(12),
    endedAt: null,
    stopRequestedAt: null,
    inputTokens: null,
    outputTokens: null,
    lineCount: 214,
    ...p,
  };
}
const CHAT_SESSIONS: Session[] = [
  chatSession({ id: "s1", startedAt: chatAgo(2880), endedAt: chatAgo(2879), outcome: "auth", note: "CLI 로그인이 안 돼 있다(outcome=auth)", lineCount: 3 }), // copy-ignore: 견본 자료
  chatSession({ id: "s2", runner: "naru-2", startedAt: chatAgo(1500), endedAt: chatAgo(1460), outcome: "ok", lineCount: 88 }),
  chatSession({ id: "s3" }),
];
const CHAT_STOPPING: Session = chatSession({ id: "s4", stopRequestedAt: chatAgo(1) });

function logEntry(p: Partial<LogEntry> & Pick<LogEntry, "seq" | "role" | "kind" | "text">): LogEntry {
  return { at: chatAgo(1), label: null, summary: null, body: null, isError: false, ...p };
}
const CHAT_LOG: LogEntry[] = [
  logEntry({ seq: 1, role: "message", kind: "prompt", text: "[수행] 이슈 「우편함 — 세션에 말을 건다」…\n\n0) reserve_tasks([...])\n1) get_task …".repeat(6) }), // copy-ignore: 견본 자료
  logEntry({ seq: 2, role: "message", kind: "message", text: "이슈를 읽었다. 우편함은 이슈 축으로 가고, 집어 가는 문과 남기는 문을 신원으로 가른다." }), // copy-ignore: 견본 자료
  logEntry({ seq: 3, role: "progress", kind: "activity", label: "읽기", summary: "crates/api/src/agent.rs", text: "Read crates/api/src/agent.rs" }), // copy-ignore: 견본 자료
  logEntry({ seq: 4, role: "progress", kind: "activity", label: "명령", summary: "cargo test -p b3x-in-api", text: "Bash", body: "test result: ok. 33 passed; 0 failed" }), // copy-ignore: 견본 자료
  logEntry({ seq: 5, role: "progress", kind: "thinking", summary: "PENDING_WHERE 를 TASK_SELECT 와 같은 문장으로 둬야 한다", text: "…" }), // copy-ignore: 견본 자료
  logEntry({ seq: 6, role: "progress", kind: "stderr", text: "warning: unused variable `who`", isError: true, summary: "warning: unused variable `who`" }),
  logEntry({ seq: 7, role: "raw", kind: "raw", text: '{"type":"unknown_event","seq":99}' }),
  logEntry({ seq: 8, role: "message", kind: "message", text: "「그 파일은 건드리지 마」 — 받았다. messages.rs 만 고친다." }), // copy-ignore: 견본 자료
];

function chatMsg(p: Partial<Message> & Pick<Message, "id">): Message {
  return {
    taskId: "t-in-chat-mailbox",
    body: "그 파일은 건드리지 마 — messages.rs 만", // copy-ignore: 견본 자료
    createdBy: "admin",
    createdAt: chatAgo(3),
    sessionId: "s3",
    deliveredAt: null,
    deliveredTo: null,
    sessionEndedAt: null,
    ...p,
  };
}
const CHAT_MSGS: Message[] = [
  chatMsg({ id: "m1" }),
  chatMsg({ id: "m2", deliveredAt: chatAgo(2), deliveredTo: "naru-1" }),
  chatMsg({ id: "m3", sessionId: "s1", sessionEndedAt: chatAgo(2879), body: "인증부터 고쳐라" }), // copy-ignore: 견본 자료
  chatMsg({ id: "m4", sessionId: null, body: "API 먼저 하고 화면은 나중에" }), // copy-ignore: 견본 자료
];

const CHAT_EVENTS: TaskEvent[] = [
  { taskId: "t-in-chat-mailbox", taskTitle: "우편함", kind: "status", actor: "roo-1", summary: "", from: "ready", to: "in-progress", at: chatAgo(90) }, // copy-ignore: 견본 자료
  { taskId: "t-in-chat-mailbox", taskTitle: "우편함", kind: "link", actor: "roo-1", summary: "session 링크를 남겼습니다: vscode://…", from: null, to: null, at: chatAgo(89) }, // copy-ignore: 견본 자료
  { taskId: "t-in-chat-mailbox", taskTitle: "우편함", kind: "receive", actor: "roo-1", summary: "우편함의 말 1건을 집어 갔습니다", from: null, to: null, at: chatAgo(60) }, // copy-ignore: 견본 자료
  { taskId: "t-in-chat-mailbox", taskTitle: "우편함", kind: "stop", actor: "human", summary: "세션을 멈추라고 했습니다 — 나루가 다음 박동에 끊습니다", from: null, to: null, at: chatAgo(5) }, // copy-ignore: 견본 자료
];

const tidyAgo = (min: number) => new Date(MOCK_NOW - min * 60_000).toISOString();
function tidyP(p: Partial<Proposal> & Pick<Proposal, "id" | "taskId" | "kind" | "reason">): Proposal {
  return { refTaskId: null, status: "open", batchId: "t-tidy-1", createdBy: "naru-1", createdAt: tidyAgo(180), resolvedAt: null, resolvedBy: null, ...p };
}
/** 시안 안 A 의 표 그대로 — 갈래 넷 + 지난 기록 둘 + 목록에 없는 이슈 하나. */
/* 알람 목업 — 갈래 여섯 × 안 읽음/읽음, 이슈가 지워진 줄, 다시 붙은 러너(끝남), 서버가 늘린 갈래. */
function mockAlarm(a: Partial<Alarm> & Pick<Alarm, "id" | "kind" | "tone" | "taskTitle" | "body">): Alarm {
  return { taskId: "t-in-x", sessionId: null, go: "task", createdAt: tidyAgo(5), read: false, ...a };
}
const ALARMS: Alarm[] = [
  mockAlarm({ id: "al-1", kind: "blocked", tone: "alert", taskId: "t-in-log-filter", taskTitle: "로그 뷰어 필터", body: "질문 대기: 표 열 순서를 어떻게 둘까?", createdAt: tidyAgo(4) }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-8", kind: "runner-down", tone: "alert", taskId: null, runner: "s103", go: "servers", taskTitle: "s103", body: "나루 연결이 5분 넘게 끊겼습니다", createdAt: tidyAgo(9) }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-2", kind: "session-failed", tone: "alert", taskId: "t-in-alarm-auto", sessionId: "ses-1", go: "session", taskTitle: "알람 구현", body: "수행 세션 — timeout: 90분 넘김", createdAt: tidyAgo(12) }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-3", kind: "rework", tone: "alert", taskId: "t-in-git-empty", taskTitle: "git 저장소 화면", body: "리뷰가 되돌림: 빈 상태 누락", createdAt: tidyAgo(31) }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-9", kind: "runner-down", tone: "alert", taskId: null, runner: "naru-1", go: "servers", taskTitle: "naru-1", body: "나루 연결이 5분 넘게 끊겼습니다", createdAt: tidyAgo(180), resolvedAt: tidyAgo(160), read: true }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-4", kind: "stale", tone: "warn", taskId: "t-in-vendor-set", taskTitle: "배차 벤더 설정", body: "진행 중인데 도는 세션 없음 · 2시간", createdAt: tidyAgo(125), read: true }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-5", kind: "review", tone: "info", taskId: "t-tidy-1001", go: "tidy", taskTitle: "정리 — 2026-10-01", body: "정리 제안 7건 — 골라 적용할 차례", createdAt: tidyAgo(60 * 20), read: true }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-6", kind: "blocked", tone: "alert", taskId: null, go: "alarms", taskTitle: "지워진 이슈", body: "auth", createdAt: tidyAgo(60 * 72), read: true }), // copy-ignore: 견본 자료
  mockAlarm({ id: "al-7", kind: "budget-x", tone: "odd", taskTitle: "모르는 갈래", body: "서버가 늘린 갈래 — 코드 그대로 보인다", createdAt: tidyAgo(60 * 80), read: true }), // copy-ignore: 견본 자료
];

const TIDY_ROWS: TidyRow[] = [
  { p: tidyP({ id: "tp-1", taskId: "t-in-log-filter", kind: "duplicate", refTaskId: "t-in-log-level", reason: "같은 결과물(레벨 칩 다중 선택). PR #98 로 병합됨" }), task: { id: "t-in-log-filter", title: "로그 거르개 — 레벨 여러 개 고르기", status: "review" }, ref: { id: "t-in-log-level", title: "로그 레벨 다중 선택", status: "done" } }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-2", taskId: "t-in-git-retry", kind: "already-done", reason: "PR #104 병합(09-28). 이슈는 검토에 남아 있다" }), task: { id: "t-in-git-retry", title: "git 시험 재시도 단추", status: "review" } }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-3", taskId: "t-in-sw-cache", kind: "stale-in-progress", reason: "열린 세션 없음 · 마지막 진행 09-27 14:02(4일 전) · 담당 held" }), task: { id: "t-in-sw-cache", title: "서비스 워커 캐시 무효화", status: "in-progress" } }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-4", taskId: "t-in-alarm-auto", kind: "design-first", refTaskId: "t-in-alarm-design", reason: "화면을 바꾸는 구현인데 선행에 시안이 없다" }), task: { id: "t-in-alarm-auto", title: "알람 — 종 · 목록", status: "ready" }, ref: { id: "t-in-alarm-design", title: "[디자인] 알람 시안", status: "review" } }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-5", taskId: "t-gone", kind: "duplicate", refTaskId: "t-also-gone", reason: "목록에 없는 이슈 — 제목을 지어내지 않는다" }) }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-6", taskId: "t-in-naru-tags", kind: "duplicate", refTaskId: "t-in-naru-cap", reason: "같은 dispatch_instance.tags", status: "applied", resolvedAt: tidyAgo(60), resolvedBy: "human" }), task: { id: "t-in-naru-tags", title: "나루 능력 태그", status: "cancelled" } }, // copy-ignore: 견본 자료
  { p: tidyP({ id: "tp-7", taskId: "t-in-sw-old", kind: "lease-expired-x", reason: "서버가 늘린 갈래 — 코드 그대로 보인다" }), task: { id: "t-in-sw-old", title: "모르는 갈래의 제안", status: "todo" } }, // copy-ignore: 견본 자료
];

/* 긴 근거 — 세 줄로 접히고 「근거 펼치기」가 붙는 줄. */
const TIDY_LONG: TidyRow[] = [
  TIDY_ROWS[0],
  {
    p: tidyP({ id: "tp-8", taskId: "t-in-ws-retry", kind: "already-done",
      reason: "PR #212 가 같은 일을 끝냈다 — 러너 WS 가 끊기면 1·2·4·8초로 다시 붙고, 붙는 동안 폴링이 안전망으로 돈다. 이 이슈의 수용 기준 셋(다시 붙음 · 폴링 유지 · 끊김 알람)이 그 PR 의 시험 셋과 한 줄씩 맞는다.\n남은 차이는 없다. 이슈는 10-21 부터 검토에 머물러 있고 그 뒤 커밋이 없다.\n완료로 옮기면 선행으로 문 두 이슈(t-in-ws-kick · t-in-runner-down)가 풀린다.\n근거 커밋: 4f1c2e9 · 9a07b11." }), // copy-ignore: 견본 자료
    task: { id: "t-in-ws-retry", title: "러너 WS 다시 붙기", status: "review" }, // copy-ignore: 견본 자료
  },
];

const noop = async () => undefined;

/* 로그인의 부품 표본 — 서버를 안 부른다. 값은 공통 ③④ 시안의 것 그대로. */
const PAT_ME: Me = {
  kind: "human",
  id: "u-admin",
  username: "admin",
  displayName: "관리자", // copy-ignore: 견본 자료
  role: "admin",
  hasPassword: true,
  totpEnabled: true,
  totpRequired: false,
  via: "local",
  expiresAt: "2026-10-15T14:02:00Z",
};
/* 2단계 표본 — 서버를 안 부른다. 등록 시작은 RFC 6238 예제 비밀(공개된 시험값)로 QR 을 그린다.
   확인 · 로그인 코드는 늘 거절해서 「서버 문장이 그대로 보이는」 상태를 세운다. */
const PAT_TOTP_FNS = {
  onSetup: async () => ({
    secret: "JBSWY3DPEHPK3PXP",
    uri: "otpauth://totp/in%20b3x:admin?secret=JBSWY3DPEHPK3PXP&issuer=in%20b3x",
  }),
  onConfirm: async () => {
    throw new Error("인증 코드가 올바르지 않습니다."); // copy-ignore: 견본 자료
  },
  onDisable: async () => undefined,
};
const patRejectCode = async () => {
  throw new Error("인증 코드가 올바르지 않습니다."); // copy-ignore: 견본 자료
};
const PAT_USERS: UserRow[] = [
  { id: "u-admin", username: "admin", displayName: "관리자", role: "admin", hasPassword: true, disabled: false, totpEnabled: true, hubLinked: false, email: "admin@irolab.co", projects: null, createdAt: "2026-10-08T00:00:00Z" }, // copy-ignore: 견본 자료
  { id: "u-kim", username: "kim", displayName: "김민지", role: "user", hasPassword: true, disabled: false, totpEnabled: false, hubLinked: true, email: "minji.kim@irolab.co", projects: ["b3x-in", "mops-code"], createdAt: "2026-10-08T00:00:00Z" }, // copy-ignore: 견본 자료
  { id: "u-lee", username: "lee", displayName: "이도윤", role: "user", hasPassword: false, disabled: false, totpEnabled: false, hubLinked: true, email: "lee@irolab.co", projects: [], createdAt: "2026-10-08T00:00:00Z" }, // copy-ignore: 견본 자료
  { id: "u-park", username: "park", displayName: "박서준", role: "user", hasPassword: true, disabled: true, totpEnabled: true, hubLinked: false, email: null, projects: null, createdAt: "2026-10-08T00:00:00Z" }, // copy-ignore: 견본 자료
];

/** 러너 표 견본의 「지금」 — 고정해 두어야 「3분 전」 같은 글자가 볼 때마다 안 바뀐다. */
const PAT_NOW = Date.parse("2026-10-25T12:00:00Z");
/** 연결 요청 견본 — 3분 전에 서버가 띄웠고 7분 남았다. */
const PAT_PAIR: RunnerPairRequest = {
  code: "K7QM-4XRT",
  name: "s114",
  host: "trx40 192.168.5.114",
  createdAt: "2026-10-25T11:57:00Z",
  expiresAt: "2026-10-25T12:07:00Z",
  approvedName: null,
  approvedBy: null,
  nameTaken: null,
};
/** MCP 연결 요청 견본 — 루프백 · 7분 남음. */
const PAT_MCP: OAuthRequest = {
  id: "mcpr-demo",
  clientName: "Claude Code",
  redirectUri: "http://127.0.0.1:33418/callback",
  expiresAt: "2026-10-25T12:07:00Z",
  approved: false,
  suggestedName: "mcp-claude-code",
};
/** git 연결 표 견본 — 연결됨 · 다시 연결 · 승인 전 한 줄씩. */
const PAT_CONN_BASE = { clientId: "demo", refreshSet: true, connectedBy: "admin", createdAt: "2026-10-20T00:00:00Z", connectedAt: "2026-10-20T00:00:00Z" };
/** 필수 체크 칩 셋(안 정함 · 이름들 · 요구 없음)과 펼친 편집 — 저장은 서버 없이 받은 값을 그대로 돌려준다. */
function PatRepoChecks() {
  const [v, setV] = useState<Checks>(["check", "api-test"]);
  const [open, setOpen] = useState(true);
  return (
    <div className="git__list">
      <div className="git__row">
        <span className="git__rowtext">
          <RepoChecksChip value={null} open={false} onToggle={() => {}} />
          <RepoChecksChip value={[]} open={false} onToggle={() => {}} />
          <RepoChecksChip value={v} open={open} onToggle={() => setOpen(!open)} />
        </span>
      </div>
      {open ? (
        <RepoChecksEditor repoId="pat" value={v} onSaved={setV} onClose={() => setOpen(false)} save={async (_id, x) => ({ requiredChecks: x })} />
      ) : null}
    </div>
  );
}


const PAT_CONNS: GitConnList = {
  redirectUri: "https://in.b3x.app/api/git-connections/callback",
  providers: ["gitea", "github", "gitlab", "bitbucket"],
  connections: [
    { ...PAT_CONN_BASE, id: "gc-gh", provider: "github", label: "GitHub · irolab-co", baseUrl: "https://github.com", tokenSet: true, expiresAt: "2026-10-25T20:00:00Z", account: "irolab-bot", lastError: null, repoCount: 4 }, // copy-ignore: 견본 자료
    { ...PAT_CONN_BASE, id: "gc-gl", provider: "gitlab", label: "사내 GitLab", baseUrl: "https://gitlab.example.com", tokenSet: true, expiresAt: null, account: "b3x-bot", lastError: "invalid_grant", repoCount: 2 }, // copy-ignore: 견본 자료
    { ...PAT_CONN_BASE, id: "gc-gt", provider: "gitea", label: "사내 Gitea", baseUrl: "https://gitea.example.com", tokenSet: false, refreshSet: false, expiresAt: null, account: null, connectedBy: null, connectedAt: null, lastError: null, repoCount: 0 }, // copy-ignore: 견본 자료
  ],
};
const PAT_RUNNERS: RunnerRow[] = [
  { name: "s114", source: "web", createdBy: "admin", createdAt: "2026-10-25T09:00:00Z", connections: 2, lastSeenAt: "2026-10-25T11:57:00Z", presence: { name: "s114", runner: "s114", tags: ["gpu"], vendors: ["claude", "chat"], version: "0.9.3", mode: "host", since: "2026-10-25T11:57:00Z" } }, // copy-ignore: 견본 자료
  { name: "s103", source: "web", createdBy: "admin", createdAt: "2026-10-20T09:00:00Z", connections: 0, lastSeenAt: "2026-10-25T11:46:00Z", downSince: "2026-10-25T11:46:00Z", presence: null }, // copy-ignore: 견본 자료
  { name: "s205", source: "env", createdBy: null, createdAt: null, connections: 1, lastSeenAt: "2026-10-25T10:00:00Z", presence: { name: "s205", runner: "s205", tags: null, vendors: ["chat"], version: "0.9.3", mode: "docker", since: "2026-10-25T10:00:00Z" } }, // copy-ignore: 견본 자료
  { name: "s205", source: "web", createdBy: "kim", createdAt: "2026-10-24T09:00:00Z", connections: 0, lastSeenAt: null, presence: null }, // copy-ignore: 견본 자료
  { name: "s111", source: "web", createdBy: "admin", createdAt: "2026-10-25T11:00:00Z", connections: 0, lastSeenAt: null, presence: null }, // copy-ignore: 견본 자료
];

/** 서버 › 서비스 견본 — 도는 중 · 멈춤(도메인) · 세팅 전(목록 값) · 받기 전 · 웹 없음(나루). */
const PAT_SERVER: Server = {
  runner: "s114",
  connections: 1,
  host: "114",
  ip: "192.168.5.114",
  reportedAt: "2026-10-25T11:58:00Z",
  services: [
    { name: "mops-issue", repo: "git@github.com:irolab-co/mops-issue.git", branch: "dev", dir: "/home/irolab/services/mops-issue", from: "env", ports: [{ key: "ISSUE_PORT", value: "8100" }], primary: "ISSUE_PORT", domain: "", state: "running", links: [{ kind: "ip", url: "http://192.168.5.114:8100", label: "192.168.5.114:8100" }] },
    { name: "mops-send", repo: "git@github.com:irolab-co/mops-send.git", branch: "dev", dir: "/home/irolab/services/mops-send", from: "env", ports: [{ key: "SEND_PORT", value: "32080" }, { key: "DB_PORT", value: "35432" }], primary: "SEND_PORT", domain: "send.mops.kr", state: "stopped", links: [{ kind: "domain", url: "https://send.mops.kr", label: "send.mops.kr" }, { kind: "ip", url: "http://192.168.5.114:32080", label: "192.168.5.114:32080" }] },
    { name: "table-mops", repo: "git@github.com:irolab-co/table-mops.git", branch: "dev", dir: "/home/irolab/services/table-mops", from: "list", ports: [{ key: "PORT", value: "8088" }], primary: "PORT", domain: "", state: "not-synced", links: [{ kind: "ip", url: "http://192.168.5.114:8088", label: "192.168.5.114:8088" }] },
    { name: "naru", repo: "git@github.com:irolab-co/b3x-in.git", branch: "dev", dir: "/home/irolab/services/naru", from: "none", ports: [], primary: null, domain: "", state: "running", links: [] },
  ],
};
const PAT_APPEND = (id: string, status: Issue["status"], service: string): Issue =>
  ({ id, title: `s114 리포 추가: ${service}`, status, type: "ops", updatedAt: "2026-10-25T11:50:00Z", ops: { host: "114", cmd: "append", repo: `irolab-co/${service}`, service } }) as Issue; // copy-ignore: 견본 자료

function Section({ id, title, lead, children }: { id: string; title: string; lead: string; children: React.ReactNode }) {
  return (
    <section className="pat__sec" id={id}>
      <h2>{title}</h2>
      <p className="muted">{lead}</p>
      {children}
    </section>
  );
}

/* 이 페이지가 값을 읽어 보여줄 토큰 전부. 한 번에 읽어 문맥으로 내려보낸다 —
   토큰마다 getComputedStyle 을 부르면 테마를 바꿀 때마다 수십 번 다시 읽는다. */
const ALL_TOKENS = [
  ...SURFACES,
  ...BORDERS,
  ...FOREGROUNDS,
  ...ACCENTS,
  ...STATUS_TOKENS.flatMap((s) => [`--${s}`, `--${s}-subtle`]),
  "--danger-solid",
  ...TYPE_ROWS.map((r) => r.token),
  ...SPACING,
  ...RADII,
  ...RADIUS_ROLES,
  ...ICON_SIZES,
  "--icon-stroke",
  ...CTL_SIZES.map((s) => `--control-${s.size}`),
  "--control-touch",
  "--gutter",
  "--dur-fast",
  "--dur-base",
];

const TokenCtx = createContext<Record<string, string>>({});

/** 토큰 값을 **실제 계산값으로** 읽어 보여준다. 손으로 베껴 적으면 토큰을 고쳤을 때
 *  이 페이지만 옛날 값을 말한다. */
function TokenValue({ name }: { name: string }) {
  const values = useContext(TokenCtx);
  return <code className="pat__val">{values[name] || "…"}</code>;
}


/** 패턴 페이지의 조작 줄은 **아무 데도 안 간다** — 누르면 곧바로 성공한 척만 한다. */
const PAT_ACTS_API: IssueActsApi = {
  setStatus: async () => ({}) as Issue,
  setAssignee: async () => ({}) as Issue,
  agentRequest: async () => ({}) as Issue,
  stopSession: async () => undefined,
  remove: async () => undefined,
  prompt: async () => ({ track: "run", prompt: "" }),
  patch: async () => ({}) as Issue,
  removeDep: async () => undefined,
  childrenReady: async () => ({ moved: [], skipped: [], openDeps: [] }),
  supplement: async () => ({}) as Issue,
  reviewRequest: async () => ({}) as Issue,
  answer: async () => ({}) as Issue,
  createChild: async () => ({}) as Issue,
};
const PAT_ACTS_FAIL: IssueActsApi = {
  ...PAT_ACTS_API,
  setAssignee: async () => {
    throw new Error("세션이 돌고 있는 이슈입니다 — 먼저 「세션 멈춤」으로 멈춘 뒤 담당을 바꿔 주세요. (HTTP 409)"); // copy-ignore: 서버 원문 견본
  },
};
/** 상세의 고치는 자리(Figma 31:2) — 편집 · 질문 · 막힘 보완 · 선행 · 하위, 그리고 보드 선택 줄. */
function PatIssueEdit() {
  const pick = (id: string) => ISSUES.find((t) => t.id === id)!;
  const blocked: Issue = {
    ...pick("t-in-odd-blocked"),
    openSession: null,
    blockedFrom: "review",
    blockedNote: "리뷰 트랙이 3번 시도했다 — 시간 초과", // copy-ignore: 서버가 적는 막힘 사유 견본
  };
  const asked: Issue = {
    ...blocked,
    openQuestions: [
      // copy-ignore: 세션이 남긴 질문 견본(서버 데이터)
      { id: "q1", prompt: "토큰 만료를 5분으로 둘까, 10분으로 둘까?", options: ["5분", "10분"], askedStatus: "in-progress", createdAt: "2026-09-12T03:00:00Z" },
    ],
  };
  const epic = pick("t-in-epic-m3");
  const withDeps: Issue = { ...pick("t-in-screen-pattern"), dependsOn: ["t-in-tokens", "t-in-gone"] };
  const two = ISSUES.filter((t) => t.status === "todo" || t.status === "ready").slice(0, 2);
  return (
    <div className="pat__grid">
      <Card title={tx("issueEdit.label")}>
        <IssueSpecEditor t={withDeps} api={PAT_ACTS_API} onSaved={() => undefined} onClose={() => undefined} />
      </Card>
      <Card title={tx("issueEdit.questions", { n: 1 })}>
        <IssueQuestions t={asked} api={PAT_ACTS_API} onChanged={() => undefined} />
      </Card>
      <Card title={tx("issueEdit.supplement")}>
        <SupplementCard t={blocked} api={PAT_ACTS_API} onChanged={() => undefined} />
      </Card>
      <Card title={tx("issueEdit.deps", { n: 2 })}>
        <DepsEditor t={withDeps} all={ISSUES} api={PAT_ACTS_API} onChanged={() => undefined} />
      </Card>
      <Card title={tx("issueEdit.children", { n: 0 })}>
        <ChildrenPanel t={withDeps} all={ISSUES} api={PAT_ACTS_API} onChanged={() => undefined} />
      </Card>
      <Card title={epic.id}>
        <ChildrenPanel t={epic} all={ISSUES} api={PAT_ACTS_API} onChanged={() => undefined} onOpen={() => undefined} />
      </Card>
      <Card title={tx("boardSel.count", { n: two.length })}>
        <BoardSelBar picked={two} api={PAT_ACTS_API} onDone={() => undefined} onClear={() => undefined} />
      </Card>
      <Card title={tx("boardSel.deleteGo")}>
        <BoardSelBar picked={two} api={PAT_ACTS_API} onDone={() => undefined} onClear={() => undefined} initialConfirm />
      </Card>
    </div>
  );
}

/** 상세의 틀 — 메타 줄은 값이 찬 것 · 빈 것 둘, 주소 복사는 기본. 모달 자체는 화면을 덮어 여기 안 세운다. */
function PatIssueDetail() {
  const base = ISSUES[0];
  const running: Issue = { ...base, projectId: "in.b3x.app", assignee: "naru-1", openSession: { id: "s-1", runner: "naru-1", startedAt: new Date(MOCK_NOW - 20 * 60000).toISOString() }, createdAt: "2026-10-28T07:50:00Z" };
  const bare: Issue = { ...base, projectId: null, assignee: null, openSession: null, createdAt: undefined };
  return (
    <div className="pat__grid">
      <Card title="running">
        <MetaLine t={running} />
      </Card>
      <Card title="empty">
        <MetaLine t={bare} />
      </Card>
      <Card title="copy">
        <CopyLinkButton id={base.id} />
      </Card>
    </div>
  );
}

/** 상세의 탭(Figma 33:2) — 다섯 탭을 하나씩 펼친 채로. 이력은 요청 없이 견본 줄로 그린다. */
function PatIssueTabs() {
  const pick = (id: string) => ISSUES.find((t) => t.id === id)!;
  const t: Issue = {
    ...pick("t-in-screen-pattern"),
    dependsOn: ["t-in-tokens"],
    handoff: "탭 다섯을 세웠고, 조작 줄은 탭 위로 올렸다. 남은 것: 맥락 칸은 b3x-in 에 없어 뺐다.", // copy-ignore: 세션이 남긴 인계 노트 견본
    attachments: [{ id: "att-1", filename: "detail-tabs.png", mime: "image/png", size: 48213, createdBy: "naru-1" }],
    createdAt: "2026-10-28T07:50:00Z",
  };
  const empty: Issue = { ...t, intent: "", acceptance: [], handoff: null, attachments: [] };
  const hist: ProgressRow[] = [
    { from: "ready", to: "in-progress", actor: "naru-1", note: null, at: "2026-10-28T07:58:00Z" },
    { from: "todo", to: "ready", actor: "o2hp", note: "나루에게 넘김", at: "2026-10-28T07:52:00Z" }, // copy-ignore: 진행 메모 견본(서버 데이터)
    { from: null, to: "todo", actor: "o2hp", note: null, at: "2026-10-28T07:50:00Z" },
  ];
  const tabs: IssueTab[] = ["intent", "acceptance", "info", "handoff", "history"];
  return (
    <div className="pat__grid">
      {tabs.map((k) => (
        <Card key={k} title={k}>
          <IssueTabs t={t} all={ISSUES} onOpen={() => undefined} onChanged={() => undefined} initialTab={k} history={hist} milestoneLabel="M9" />
        </Card>
      ))}
      <Card title="empty">
        <IssueTabs t={empty} all={[]} onOpen={() => undefined} onChanged={() => undefined} initialTab="handoff" history={[]} />
      </Card>
    </div>
  );
}

function PatIssueActs() {
  const pick = (id: string) => ISSUES.find((t) => t.id === id)!;
  const base = pick("t-in-screen-pattern");
  const rows: [string, Issue, IssueActsApi?, ("cancel" | "handoff" | "delete" | "move")?][] = [
    ["ready", { ...base, status: "ready", assignee: "admin", assigneeKind: "ticket" }],
    ["held", { ...pick("t-in-odd-blocked"), openSession: null }],
    ["running", pick("t-in-screen-board")],
    ["epic", pick("t-in-epic-m3")],
    ["cancelled", pick("t-in-odd-cancelled")],
    ["requested", { ...base, status: "ready", assignee: null, assigneeKind: null, agentRequestedAt: "2026-09-12T03:00:00Z" }],
    ["error", { ...base, status: "ready", assignee: "admin", assigneeKind: "ticket" }, PAT_ACTS_FAIL],
    ["cancel", { ...pick("t-in-odd-blocked"), openSession: null }, undefined, "cancel"],
    ["handoff", { ...pick("t-in-odd-blocked"), openSession: null }, undefined, "handoff"],
    ["delete", pick("t-in-epic-m3"), undefined, "delete"],
    ["reviewRerun", { ...pick("t-in-odd-blocked"), openSession: null, assignee: null, assigneeKind: null, blockedFrom: "review" }],
    ["planRun", { ...base, status: "todo", intent: "", acceptance: [], assignee: null, assigneeKind: null }],
    ["move", { ...base, status: "in-progress", assignee: null, assigneeKind: null }, undefined, "move"],
  ];
  return (
    <div className="pat__grid">
      {rows.map(([k, t, api, mode]) => (
        <Card key={k} title={t.id}>
            <IssueActions
              t={t}
              me="admin"
              api={api ?? PAT_ACTS_API}
              initialMode={mode}
              onChanged={() => undefined}
              onDeleted={() => undefined}
            />
        </Card>
      ))}
    </div>
  );
}

export function PatternsPage() {
  const [q, setQ] = useState("");
  const tokens = useTokenValues(ALL_TOKENS);

  /* 사이드바는 이 화면의 맥락 메뉴라 이 화면이 그린다. 주소는 해시 하나가 화면을 정하므로
     본문 앵커(#type)를 못 쓴다 — 화면 주소를 덮어쓴다. 그래서 스크롤로 옮긴다. */
  const side = (
    <>
      <SideGroup>{tx("pat.side")}</SideGroup>
      {PATTERN_SECTIONS.map((s) => (
        <SideAction key={s.id} icon="hash" onClick={() => document.getElementById(s.id)?.scrollIntoView({ block: "start" })}>
          {s.label}
        </SideAction>
      ))}
    </>
  );

  return (
    <TokenCtx.Provider value={tokens}>
    <AppShell route="/patterns" sidebarTitle={tx("pat.menu")} sidebar={side}>
    <div className="pat">
      <PageHead title={tx("nav.patterns")} lead={tx("pat.lead")} />

      <Section
        id="type"
        title={tx("pat.nav.type")}
        lead={tx("pat.type.lead")}
      >
        <div className="pat__type">
          {TYPE_ROWS.map((r) => (
            <div className="pat__typerow" key={r.tag}>
              <div className="pat__typemeta">
                <code>{r.token}</code>
                <TokenValue name={r.token} />
              </div>
              <div className={`pat__typesample pat__typesample--${r.tag}`}>{r.sample}</div>
            </div>
          ))}
        </div>
        <h3>{tx("pat.weight.title")}</h3>
        <div className="pat__type">
          {WEIGHTS.map((w) => (
            <div className="pat__typerow" key={w.weight}>
              <div className="pat__typemeta">
                <code>{w.weight}</code>
                <small>{w.use}</small>
              </div>
              <div className="pat__typesample" style={{ fontWeight: w.weight }}>
                {tx("pat.type.h1")} · 0123456789
              </div>
            </div>
          ))}
        </div>
        <p className="caption">
          {rich(tx("pat.weight.tnum", hole("table", "tnum")), { table: <code>table</code>, tnum: <code>.tnum</code> })}{" "}
          <span className="tnum">11:11 · 88:88</span> / {tx("pat.weight.prop")} 11:11 · 88:88
        </p>
      </Section>

      <Section id="color" title={tx("pat.nav.color")} lead={tx("pat.color.lead")}>
        <h3>{tx("pat.color.surface")}</h3>
        <p className="muted">{rich(tx("pat.color.surfaceLead", hole("canvas")), { canvas: <code>--canvas</code> })}</p>
        <div className="pat__swatches">
          {SURFACES.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__chipcolor" style={{ background: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.color.border")}</h3>
        <p className="muted">
          {rich(tx("pat.color.borderLead", hole("strong", "control")), {
            strong: <code>--hairline-strong</code>,
            control: <code>--border-control</code>,
          })}
        </p>
        <div className="pat__swatches">
          {BORDERS.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__chipline" style={{ borderColor: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.color.ink")}</h3>
        <div className="pat__swatches">
          {FOREGROUNDS.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__fgsample" style={{ color: `var(${t})` }}>
                {tx("pat.color.glyphs")}
              </span>
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.color.accent")}</h3>
        <div className="pat__swatches">
          {ACCENTS.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__chipcolor" style={{ background: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.color.status")}</h3>
        <table className="pat__table">
          <thead>
            <tr>
              <th>{tx("field.status")}</th>
              <th>{tx("pat.color.fg")}</th>
              <th>{tx("pat.color.subtle")}</th>
            </tr>
          </thead>
          <tbody>
            {STATUS_TOKENS.map((s) => (
              <tr key={s}>
                <td>
                  <code>--{s}</code>
                </td>
                <td>
                  <span className="pat__chipcolor" style={{ background: `var(--${s})` }} />{" "}
                  <span style={{ color: `var(--${s})` }}>{tx("pat.color.glyphs")}</span> <TokenValue name={`--${s}`} />
                </td>
                <td>
                  <span className="pat__softbox" style={{ background: `var(--${s}-subtle)` }} />
                  <TokenValue name={`--${s}-subtle`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="caption">
          {rich(tx("pat.color.contrast", hole("tertiary")), { tertiary: <code>--ink-tertiary</code> })}
        </p>
      </Section>

      <Section id="space" title={tx("pat.space.title")} lead={tx("pat.space.lead")}>
        <h3>{tx("pat.space.space")}</h3>
        <div className="pat__rule">
          {SPACING.map((t) => (
            <div className="pat__ruleitem" key={t}>
              <span className="pat__rulebar" style={{ width: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>
        <p className="caption">
          {rich(tx("pat.space.gutter", hole("name", "value")), { name: <code>--gutter</code>, value: <TokenValue name="--gutter" /> })}
        </p>

        <h3>{tx("pat.space.radius")}</h3>
        <div className="pat__swatches">
          {RADII.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__radius" style={{ borderRadius: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.space.radiusRoles")}</h3>
        <p className="muted">{tx("pat.space.radiusRolesLead")}</p>
        <div className="pat__swatches">
          {RADIUS_ROLES.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__radius" style={{ borderRadius: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.space.shadow")}</h3>
        <p className="muted">
          {rich(tx("pat.space.shadowLead", hole("float", "edge")), { float: <code>--shadow-float</code>, edge: <code>--shadow-edge</code> })}
        </p>
        <div className="pat__swatches">
          {SHADOWS.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__shadow" style={{ boxShadow: `var(${t})` }} />
              <code>{t}</code>
            </div>
          ))}
        </div>
        <h3>{tx("pat.space.iconSize")}</h3>
        <p className="muted">
          {rich(tx("pat.space.iconLead", hole("stroke", "strokeV", "fast", "fastV", "base", "baseV", "ease")), {
            stroke: <code>--icon-stroke</code>,
            strokeV: <TokenValue name="--icon-stroke" />,
            fast: <code>--dur-fast</code>,
            fastV: <TokenValue name="--dur-fast" />,
            base: <code>--dur-base</code>,
            baseV: <TokenValue name="--dur-base" />,
            ease: <code>--ease-out</code>,
          })}
        </p>
        <div className="pat__swatches">
          {ICON_SIZES.map((t) => (
            <div className="pat__sw" key={t}>
              <span className="pat__iconbox" style={{ width: `var(${t})`, height: `var(${t})` }} />
              <code>{t}</code>
              <TokenValue name={t} />
            </div>
          ))}
        </div>

        <h3>{tx("pat.space.focus")}</h3>
        <p className="muted">{rich(tx("pat.space.focusLead", hole("ring")), { ring: <code>--focus-ring</code> })}</p>
        <div className="pat__row">
          <Button>{tx("pat.space.focusTry")}</Button>
          <span className="b-btn b-btn-secondary pat__ringdemo" tabIndex={-1}>
            {tx("pat.space.focusAlways")}
          </span>
        </div>
      </Section>

      <Section
        id="spec"
        title={tx("pat.nav.spec")}
        lead={tx("pat.spec.lead")}
      >
        <table className="pat__table">
          <thead>
            <tr>
              <th>{tx("pat.spec.step")}</th>
              <th>{tx("pat.spec.height")}</th>
              <th>{tx("pat.spec.where")}</th>
            </tr>
          </thead>
          <tbody>
            {CTL_SIZES.map((s) => (
              <tr key={s.size}>
                <td>
                  <code>{s.size}</code>
                </td>
                <td>
                  <TokenValue name={`--control-${s.size}`} />
                </td>
                <td>{s.where}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>{tx("pat.spec.lineup")}</h3>
        {CTL_SIZES.map((s) => (
          <div className="pat__row pat__row--baseline" key={s.size}>
            <code className="pat__sizetag">{s.size}</code>
            <Button size={s.size} variant="primary">
              {tx("pat.spec.run")}
            </Button>
            <Button size={s.size}>{tx("common.action.cancel")}</Button>
            {s.size === "sm" ? (
              <span className="caption">{tx("pat.spec.noSm")}</span>
            ) : (
              <>
                <Input size={s.size} defaultValue={tx("pat.spec.input")} style={{ width: "14ch" }} />
                <Select size={s.size} defaultValue="a">
                  <option value="a">{tx("pat.spec.dropdown")}</option>
                  <option value="b">{tx("pat.spec.second")}</option>
                </Select>
              </>
            )}
            {s.size === "sm" ? (
              <>
                <Chip>{tx("pat.states.badge")}</Chip>
                <button type="button" className="b-chip is-on" aria-pressed>
                  {tx("pat.spec.chipOn")}
                </button>
                <button type="button" className="b-chip" aria-pressed={false}>
                  {tx("pat.spec.chip")}
                </button>
              </>
            ) : null}
          </div>
        ))}
      </Section>

      <Section
        id="states"
        title={tx("pat.statesSec.title")}
        lead={tx("pat.statesSec.lead")}
      >
        <PatternStates />
      </Section>

      <Section
        id="buttons"
        title={tx("pat.states.button")}
        lead={tx("pat.buttons.lead")}
      >
        <table className="pat__table pat__table--states">
          <thead>
            <tr>
              <th>{tx("pat.buttons.variant")}</th>
              <th>{tx("pat.states.base")}</th>
              <th>{tx("pat.states.disabled")}</th>
              <th>{tx("pat.states.loading")}</th>
              <th>{tx("pat.buttons.iconOnly")}</th>
            </tr>
          </thead>
          <tbody>
            {VARIANTS.map((v) => (
              <tr key={v}>
                <td>
                  <code>{v}</code>
                </td>
                <td>
                  <Button variant={v}>{tx("pat.buttons.ok")}</Button>
                </td>
                <td>
                  <Button variant={v} disabled>
                    {tx("pat.buttons.ok")}
                  </Button>
                </td>
                <td>
                  <Button variant={v} loading>
                    {tx("pat.buttons.sending")}
                  </Button>
                </td>
                <td>
                  <Button variant={v} icon aria-label={tx("common.action.close")}>
                    <Icon name="x" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="form" title={tx("pat.nav.form")} lead={tx("pat.form.lead")}>
        <div className="pat__grid">
          <Field label={tx("pat.form.olaAddress")} hint={tx("pat.form.olaHint")}>
            {(id) => <Input id={id} placeholder="https://" value={q} onChange={(e) => setQ(e.target.value)} />}
          </Field>
          <Field label={tx("conn.token")} error={tx("pat.sample.tokenEmpty")}>
            {(id, invalid) => <Input id={id} type="password" invalid={invalid} defaultValue="" />}
          </Field>
          <Field label={tx("conn.model")} hint={tx("pat.form.modelHint")}>
            {(id) => (
              <Select id={id} defaultValue="">
                <option value="">{tx("pat.form.none")}</option>
                <option value="a">gpt-oss-120b</option>
              </Select>
            )}
          </Field>
          <Field label={tx("pat.form.disabled")} hint={tx("pat.form.disabledHint")}>
            {(id) => <Input id={id} disabled defaultValue={tx("pat.sample.locked")} />}
          </Field>
        </div>
      </Section>

      <Section
        id="status"
        title={tx("pat.nav.status")}
        lead={tx("pat.statusSec.lead")}
      >
        <div className="pat__row">
          {STATUS_KEYS.map((k) => (
            <StatusPill key={k} status={k} />
          ))}
        </div>
        <div className="pat__row">
          {STATUS_KEYS.map((k) => (
            <StatusPill key={k} status={k} />
          ))}
        </div>
        <div className="pat__row">
          <Chip tone="accent">{tx("pat.sample.epic")}</Chip>
          <Chip tone="info">{tx("issue.held")}</Chip>
          <Chip tone="warning">{tx("issue.merging")}</Chip>
          <Chip>{tx("issue.complexity.normal")}</Chip>
        </div>
      </Section>

      <Section id="feedback" title={tx("pat.feedback.title")} lead={tx("pat.feedback.lead")}>
        <div className="pat__stack">
          <Alert tone="info">{tx("pat.feedback.info")}</Alert>
          <Alert tone="success">{tx("pat.feedback.success")}</Alert>
          <Alert tone="warning">{tx("pat.feedback.warning")}</Alert>
          <Alert tone="error">{tx("pat.feedback.error")}</Alert>
        </div>

        <div className="pat__row">
          <Spinner size="sm" /> <span className="caption">sm</span>
          <Spinner /> <span className="caption">md</span>
          <Spinner size="lg" /> <span className="caption">lg</span>
          <span className="muted">{tx("pat.feedback.spinner")}</span>
        </div>

        <div className="pat__grid">
          <EmptyState
            title={tx("pat.feedback.emptyTitle")}
            desc={tx("pat.feedback.emptyDesc")}
            action={
              <Button variant="primary" size="md">
                <Icon name="plus" /> {tx("pat.feedback.newIssue")}
              </Button>
            }
          />
          <Card title={tx("pat.states.card")} meta={<StatusPill status="in-progress" working />}>
            <p className="muted">{tx("pat.feedback.card")}</p>
            <div className="pat__row">
              <Button size="md">{tx("pat.feedback.open")}</Button>
              <Button size="md" variant="ghost">
                {tx("pat.feedback.hide")}
              </Button>
            </div>
          </Card>
        </div>
      </Section>

      <Section
        id="rail"
        title={tx("pat.nav.rail")}
        lead={tx("pat.rail.lead")}
      >
        <div className="pat__rails">
          <div className="pat__rail" aria-label={tx("pat.rail.rowsLabel")}>
            <SideGroup>{tx("pat.rail.rows")}</SideGroup>
            <SideAction icon="hash" onClick={() => undefined}>
              {tx("pat.states.base")}
            </SideAction>
            <SideAction icon="hash" active onClick={() => undefined}>
              {tx("pat.rail.selected")}
            </SideAction>
            <SideAction icon="hash" disabled onClick={() => undefined}>
              {tx("pat.states.disabled")}
            </SideAction>
            <SideLink icon="hash" href="#/patterns">
              {tx("pat.rail.link")}
            </SideLink>
            <SideAction icon="hash" onClick={() => undefined}>
              {tx("pat.rail.long")}
            </SideAction>
          </div>
          <div className="pat__rail" aria-label={tx("pat.rail.countLabel")}>
            <SideGroup>{tx("field.status")}</SideGroup>
            <SideAction icon="circleDot" active count={12} onClick={() => undefined}>
              {tx("tidy.status.open")}
            </SideAction>
            <SideAction icon="circleCheck" count={3} onClick={() => undefined}>
              {tx("tidy.status.applied")}
            </SideAction>
            <SideAction icon="hash" count={null} onClick={() => undefined}>
              {tx("pat.rail.noCount")}
            </SideAction>
            <SideGroup>{tx("alarm.table.kind")}</SideGroup>
            <SideCheck checked onChange={() => undefined}>
              {tx("pat.rail.checkOn")}
            </SideCheck>
            <SideCheck checked={false} onChange={() => undefined}>
              {tx("pat.rail.checkOff")}
            </SideCheck>
            <SideCheck checked onChange={() => undefined} count={4}>
              {tx("pat.rail.checkOnCount")}
            </SideCheck>
            <SideNote>{tx("pat.rail.note")}</SideNote>
          </div>
          <div className="pat__rail pat__rail--railed b-sidenav is-collapsed" aria-label={tx("pat.rail.railedLabel")}>
            <SideRail>
              <SideGroup>{tx("pat.rail.rows")}</SideGroup>
              <SideAction icon="bot" onClick={() => undefined}>
                {tx("pat.states.base")}
              </SideAction>
              <SideAction icon="users" active onClick={() => undefined}>
                {tx("pat.rail.selected")}
              </SideAction>
              <SideGroup>{tx("field.status")}</SideGroup>
              <SideAction icon="circleDot" count={12} onClick={() => undefined}>
                {tx("tidy.status.open")}
              </SideAction>
              <SideCheck checked onChange={() => undefined}>
                {tx("pat.rail.checkOn")}
              </SideCheck>
            </SideRail>
          </div>
        </div>
      </Section>

      <Section
        id="page"
        title={tx("pat.nav.page")}
        lead={tx("pat.page.lead")}
      >
        <h3>{tx("pat.page.head")}</h3>
        <div className="pat__stack pat__pagehead">
          <PageHead title={tx("nav.board")} />
          <PageHead title={tx("nav.session")} lead={tx("pat.page.sessionLead")} />
          <PageHead
            title={tx("nav.tidy")}
            meta={<Chip tone="muted">○ {tx("tidy.status.open")} {tx("alarm.page.count", { n: 12 })}</Chip>}
            lead={
              <>
                <p>{tx("pat.page.lead1")}</p>
                <p>{tx("pat.page.lead2")}</p>
              </>
            }
          />
          <PageHead
            title={tx("alarm.title")}
            meta={<Chip tone="muted">{tx("alarm.page.count", { n: 3 })}</Chip>}
            actions={
              <Button size="md" variant="ghost">
                {tx("alarm.readAll")}
              </Button>
            }
          />
        </div>
        <h3>{tx("pat.page.form")}</h3>
        <FormSplit
          aside={
            <StatCard
              rows={[
                { label: tx("conn.stat.address"), value: "http://ola-mops.local:30088" },
                { label: tx("chat.tokens"), value: tx("conn.stat.set") },
                { label: tx("conn.model"), value: tx("conn.stat.unknown") },
                { label: tx("conn.stat.test"), value: tx("conn.stat.notRun") },
              ]}
            />
          }
        >
          <Field label={tx("conn.address")} hint={tx("pat.page.formHint")}>
            {(id) => <Input id={id} defaultValue="http://ola-mops.local:30088" />}
          </Field>
        </FormSplit>
        <h3>{tx("pat.page.states")}</h3>
        <div className="pat__grid">
          <Loading text={tx("board.loading")} />
          <EmptyState title={tx("pat.feedback.emptyTitle")} desc={tx("pat.page.emptyDesc")} />
          <Alert tone="error">{tx("pat.page.error")}</Alert>
        </div>
      </Section>

      <Section id="pwa" title={tx("pat.nav.pwa")} lead={tx("pat.pwa.lead")}>
        <div className="pat__stack">
          <PwaNoticeSample updateWaiting reachable={null} />
          <PwaNoticeSample updateWaiting={false} reachable={false} />
          <PwaNoticeSample updateWaiting={false} reachable={false} checking />
        </div>
      </Section>

      <Section id="icons" title={tx("pat.nav.icons")} lead={tx("pat.icons.lead")}>
        <div className="pat__icons">
          {ICON_NAMES.map((n) => (
            <div className="pat__icon" key={n}>
              <Icon name={n} className="icon--lg" />
              <code>{n}</code>
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="chat"
        title={tx("pat.nav.chat")}
        lead={tx("pat.chat.lead")}
      >
        <h3>{tx("pat.chat.strip")}</h3>
        <div className="pat__stack">
          <StageStrip cells={CHAT_STAGES} selected="run" onSelect={() => undefined} />
          <StageStrip cells={CHAT_STAGES_FAILED} selected={null} onSelect={() => undefined} />
        </div>

        <h3>{tx("pat.chat.tabs")}</h3>
        <div className="pat__stack">
          <SessionTabs sessions={CHAT_SESSIONS} selected="s3" onSelect={() => undefined} footprintCount={4} />
          <SessionTabs sessions={[CHAT_STOPPING]} selected={FOOTPRINT_TAB} onSelect={() => undefined} footprintCount={null} />
          <SessionTabs sessions={[]} selected={FOOTPRINT_TAB} onSelect={() => undefined} footprintCount={0} />
        </div>

        <h3>{tx("pat.chat.rows")}</h3>
        <div className="pat__chatbox">
          {CHAT_LOG.map((e) => (
            <LogRow key={e.seq} e={e} who={`${tx("issue.heldShort")} · claude`} />
          ))}
          <SessionNote track="run" note="CLI 로그인이 안 돼 있다(outcome=auth)" />{/* copy-ignore: 견본 */}
          {CHAT_MSGS.map((m) => (
            <HumanRow key={m.id} m={m} />
          ))}
        </div>

        <h3>{tx("pat.chat.footprint")}</h3>
        <div className="pat__chatbox">
          {CHAT_EVENTS.map((e, i) => (
            <FootprintRow key={i} e={e} />
          ))}
        </div>

        <h3>{tx("pat.chat.input")}</h3>
        <div className="pat__stack">
          <ChatInput hint={tx("chat.hint.running")} onSend={noop} onStop={noop} />
          <ChatInput hint={tx("pat.chat.stoppingHint")} onSend={noop} onStop={noop} stopping />
          <ChatInput hint={tx("pat.buttons.sending")} onSend={noop} onStop={noop} sending />
          <ChatInput hint={tx("chat.hint.noSession")} onSend={noop} />
          <ChatInput hint={tx("chat.hint.closed")} onSend={noop} disabled />
        </div>

        <h3>{tx("pat.chat.empties")}</h3>
        <div className="pat__grid">
          <EmptyState title={tx("pat.states.noSessions")} desc={tx("pat.chat.noSessionsDesc")} />
          <EmptyState title={tx("chat.noLog.title")} desc={tx("pat.chat.noLogDesc")} />
          <Alert tone="error">{tx("pat.chat.logError")}</Alert>
        </div>
      </Section>

      <Section id="issue" title={tx("pat.nav.issue")} lead={tx("pat.issue.lead")}>
        <div className="pat__grid">
          {ISSUES.filter((t) =>
            ["t-in-epic-m3", "t-in-screen-pattern", "t-in-screen-board", "t-in-odd-long", "t-in-odd-cancelled"].includes(t.id),
          ).map((t) => (
            <IssueCard
              key={t.id}
              t={t}
              milestoneLabel={t.milestoneId ? (MILESTONES[t.milestoneId] ?? t.milestoneId) : undefined}
              now={MOCK_NOW}
              onOpen={() => undefined}
              onFilterEpic={t.parentId ? () => undefined : undefined}
            />
          ))}
        </div>
        <h3>{tx("pat.issue.acts")}</h3>
        <PatIssueActs />
        <h3>{tx("pat.issue.edit")}</h3>
        <PatIssueEdit />
        <h3>{tx("pat.issue.tabs")}</h3>
        <PatIssueTabs />
        <h3>{tx("pat.issue.detail")}</h3>
        <PatIssueDetail />
      </Section>
      <Section id="dash" title={tx("pat.nav.dash")} lead={tx("pat.dash.lead")}>
        <DashStates />
      </Section>
      <Section id="ms" title={tx("pat.nav.ms")} lead={tx("pat.ms.lead")}>
        <MilestoneStates />
      </Section>
      <Section id="projects" title={tx("pat.nav.projects")} lead={tx("pat.projects.lead")}>
        <ProjectStates />
      </Section>
      <Section id="ops" title={tx("pat.nav.ops")} lead={tx("pat.ops.lead")}>
        <OpsStates />
      </Section>
      <Section id="hist" title={tx("pat.nav.hist")} lead={tx("pat.hist.lead")}>
        <HistoryStates />
      </Section>
      <Section id="queue" title={tx("pat.nav.queue")} lead={tx("pat.queue.lead")}>
        <QueueStates />
      </Section>
      <Section id="graph" title={tx("pat.nav.graph")} lead={tx("pat.graph.lead")}>
        <GraphStates />
      </Section>
      <Section
        id="git"
        title={tx("pat.nav.git")}
        lead={tx("pat.git.lead")}
      >
        <h3>{tx("pat.git.conns")}</h3>
        <GitConnSection data={PAT_CONNS} legacy={{ baseUrl: "http://gitea:3000", tokenSet: true, repoCount: 7 }} onLegacyFix={() => {}} onChanged={() => {}} />
        <RepoFilterStates />
        <h3>{tx("pat.git.test")}</h3>
        <p className="caption">{rich(tx("pat.git.testNote", hole("notError")), { notError: <b>{tx("pat.git.notError")}</b> })}</p>
        <div className="pat__stack">
          {TEST_KINDS.map((k) => {
            const b = GIT_BANNER[k];
            return (
              <Alert key={k} tone={b.tone}>
                <span aria-hidden="true">{b.glyph}</span> <b>{b.head}</b> — {b.body}
              </Alert>
            );
          })}
        </div>

        <h3>{tx("pat.git.strip")}</h3>
        <div className="pat__stack">
          <div className="git__strip">
            <div className="git__striphead">
              <span className="git__glyph git__glyph--ok" aria-hidden="true">
                ✓
              </span>
              <b className="git__striphead-label">{tx("conn.result.ok.head")}</b>
              <span className="muted">http://gitea:3000 · {tx("git.tokenSet")} · {tx("git.repoCount", { n: 12 })}</span>
              <span className="git__spacer" />
              <button type="button" className="b-btn b-btn-secondary b-btn-sm git__toggle" aria-expanded={false}>
                {tx("git.fix")} ⌄
              </button>
            </div>
          </div>
          <div className="git__strip">
            <div className="git__striphead">
              <span className="git__glyph" aria-hidden="true">
                ·
              </span>
              <b className="git__striphead-label">{tx("git.notConnected")}</b>
              <span className="muted">{tx("git.notConnectedHint")}</span>
              <span className="git__spacer" />
              <button type="button" className="b-btn b-btn-secondary b-btn-sm git__toggle" aria-expanded>
                {tx("git.fix")} ⌃
              </button>
            </div>
          </div>
        </div>

        <h3>{tx("pat.git.checks")}</h3>
        <PatRepoChecks />

        <h3>{tx("pat.git.rows")}</h3>
        <p className="caption">
          {rich(tx("pat.git.nullNote", hole("null", "unknown")), { null: <code>null</code>, unknown: <b>{tx("pat.git.nullUnknown")}</b> })}
        </p>
        <ul className="git__list">
          <li className="git__row">
            <span className="git__glyph git__glyph--ok" aria-hidden="true">
              ■
            </span>
            <span className="git__rowtext">
              <span className="git__reponame">b3x/b3x-in</span>
              <span className="muted">{usageText(14)}</span>
            </span>
            <span className="git__spacer" />
            <button type="button" className="b-btn b-btn-ghost b-btn-sm git__unlink">
              {tx("git.detach")}
            </button>
          </li>
          <li className="git__row">
            <span className="git__glyph git__glyph--ok" aria-hidden="true">
              ■
            </span>
            <span className="git__rowtext">
              <span className="git__reponame">b3x/demo</span>
              <span className="muted">{usageText(null)}</span>
            </span>
            <span className="git__spacer" />
            <button type="button" className="b-btn b-btn-ghost b-btn-sm git__unlink">
              {tx("git.detach")}
            </button>
          </li>
          <li className="git__row">
            <span className="git__glyph" aria-hidden="true">
              □
            </span>
            <span className="git__rowtext">
              <span className="git__reponame">b3x/sandbox</span>
              <span className="muted">{tx("git.notYetOn")}</span>
            </span>
            <span className="git__spacer" />
            <Button size="sm">{tx("git.put")}</Button>
          </li>
        </ul>
        <p className="git__warn">{tx("git.detachWarn")}</p>

        <h3>{tx("pat.git.empties")}</h3>
        <div className="pat__stack">
          <EmptyState title={tx("git.notConnected")} desc={tx("git.notConnectedDesc")} />
          <EmptyState title={tx("git.giteaEmpty")} desc={tx("git.giteaEmptyDesc")} />
          <EmptyState title={tx("git.emptyAttached")} desc={tx("git.emptyAttachedDesc")} />
        </div>

        <h3>{tx("pat.git.create")}</h3>
        <div className="pat__stack">
          <RepoCreateForm />
          <RepoCreateForm open />
          <RepoCreateForm open busy error="호스트가 리포 만들기를 막았습니다. 토큰에 write:organization 범위가 필요합니다 — 연결을 다시 승인하거나 그 범위를 가진 토큰으로 바꿔 주세요." />{/* copy-ignore: 서버 원문 견본 */}
        </div>
      </Section>

      <Section
        id="code"
        title={tx("pat.nav.code")}
        lead={tx("pat.code.lead")}
      >
        <h3>{tx("pat.git.test")}</h3>
        <p className="caption">{rich(tx("pat.code.testNote", hole("notError")), { notError: <b>{tx("pat.git.notError")}</b> })}</p>
        <div className="pat__stack">
          {CM_TEST_KINDS.map((k) => {
            const b = CM_BANNER[k];
            return (
              <Alert key={k} tone={b.tone}>
                <span aria-hidden="true">{b.glyph}</span> <b>{b.head}</b> — {b.body}
              </Alert>
            );
          })}
        </div>

        <h3>{tx("pat.code.strip")}</h3>
        <div className="pat__stack">
          <div className="git__strip">
            <div className="git__striphead">
              <span className="git__glyph git__glyph--ok" aria-hidden="true">
                ✓
              </span>
              <b className="git__striphead-label">{tx("conn.result.ok.head")}</b>
              <span className="muted">http://mops-code.local:8089/mcp · {tx("git.tokenSet")} · {tx("code.toolCount", { n: 24 })}</span>
              <span className="git__spacer" />
              <button type="button" className="b-btn b-btn-secondary b-btn-sm git__toggle" aria-expanded={false}>
                {tx("git.fix")} ⌄
              </button>
            </div>
          </div>
          <div className="git__strip">
            <div className="git__striphead">
              <span className="git__glyph" aria-hidden="true">
                ·
              </span>
              <b className="git__striphead-label">{tx("git.notConnected")}</b>
              <span className="muted">{tx("git.notConnectedHint")}</span>
              <span className="git__spacer" />
              <button type="button" className="b-btn b-btn-secondary b-btn-sm git__toggle" aria-expanded>
                {tx("git.fix")} ⌃
              </button>
            </div>
          </div>
          <div className="pat__row">
            <Button variant="primary" loading>
              {tx("conn.test")}
            </Button>
            <span className="caption">{tx("pat.code.testing")}</span>
          </div>
        </div>

        <h3>{tx("pat.code.tokens")}</h3>
        <div className="pat__grid">
          <Field label={tx("chat.tokens")} hint={tx("code.tokenHintSet")}>
            {(id) => (
              <div className="cm__tokenrow">
                <Input id={id} type="password" defaultValue="" placeholder={tx("conn.tokenPhSet")} />
                <Button size="lg">{tx("code.clear")}</Button>
              </div>
            )}
          </Field>
          <Field label={tx("chat.tokens")} hint={tx("code.tokenHintNone")}>
            {(id) => (
              <div className="cm__tokenrow">
                <Input id={id} type="password" defaultValue="" placeholder={tx("conn.tokenPhNone")} />
                <Button size="lg" disabled>
                  {tx("code.clear")}
                </Button>
              </div>
            )}
          </Field>
          <Field label={tx("chat.tokens")} hint={tx("code.tokenHintClear")}>
            {(id) => (
              <div className="cm__tokenrow">
                <Input id={id} type="password" defaultValue="" placeholder={tx("conn.tokenPhNone")} />
                <Button size="lg">{tx("code.clear")}</Button>
              </div>
            )}
          </Field>
        </div>

        <h3>{tx("pat.code.knows")}</h3>
        <div className="pat__stack">
          <div className="cm__info">
            <b>{tx("code.knows.title")}</b>
            <ul>
              <li>{tx("code.knows.mcp")}</li>
              <li>{tx("code.knows.repo")}</li>
              <li>{tx("code.knows.index")}</li>
            </ul>
          </div>
          <p className="cm__note">
            <span aria-hidden="true">ⓘ</span> {tx("code.emptyNote")}
          </p>
        </div>
      </Section>

      <Section
        id="vendors"
        title={tx("pat.nav.vendors")}
        lead={tx("pat.vendors.lead")}
      >
        <h3>{tx("pat.vendors.table")}</h3>
        <p className="caption">{tx("pat.vendors.tableNote")}</p>
        <div className="vendors__table" role="table" aria-label={tx("settings.vendors.table")}>
          <div className="vendors__row vendors__row--head" role="row">
            <span className="vendors__track" role="columnheader">
              {tx("settings.vendors.track")}
            </span>
            <span role="columnheader">{tx("settings.vendors.vendor")}</span>
          </div>
          {TRACKS.filter((t) => t.key !== "merge").map((t, i) => (
            <div className="vendors__row" role="row" key={t.key}>
              <span className="vendors__track" role="rowheader">
                {t.label}
              </span>
              <Select size="md" defaultValue={i === 1 ? "claude" : ""} disabled>
                <option value="">{tx("settings.vendors.unset")}</option>
                <option value="claude">claude</option>
                <option value="chat">chat</option>
              </Select>
            </div>
          ))}
        </div>

        <h3>{tx("pat.vendors.saveBar")}</h3>
        <div className="pat__stack">
          <span>
            {tx("settings.vendors.unsaved")}
            <Chip tone="warning">{tx("settings.vendors.changed", { track: tx("chat.track.design") })}</Chip>
          </span>
        </div>

        <h3>{tx("pat.vendors.noFallback")}</h3>
        <p className="caption">{tx("pat.vendors.noFallbackNote")}</p>
      </Section>

      <Section
        id="tidy"
        title={tx("pat.nav.tidy")}
        lead={tx("pat.tidy.lead")}
      >
        <h3>{tx("pat.tidy.kinds")}</h3>
        <div className="pat__row">
          {KIND_KEYS.map((k) => (
            <KindChip key={k} kind={k} />
          ))}
          <KindChip kind="lease-expired-x" />
          <ProposalStatusChip status="open" />
          <ProposalStatusChip status="applied" />
          <ProposalStatusChip status="dismissed" />
        </div>
        <h3>{tx("pat.tidy.scope")}</h3>
        <div className="pat__grid">
          <TidyScope state={{ anchor: null, lastSweepAt: null }} openCount={42} onRequest={() => undefined} now={MOCK_NOW} />
          <TidyScope state={{ anchor: null, lastSweepAt: tidyAgo(180) }} openCount={42} onRequest={() => undefined} now={MOCK_NOW} />
          <TidyScope state={{ anchor: { id: "t-tidy-1", title: "정리 — 2026-09-12", status: "ready", requestedAt: tidyAgo(1), running: false }, lastSweepAt: tidyAgo(180) }} openCount={42} onRequest={() => undefined} now={MOCK_NOW} />{/* copy-ignore: 견본 */}
          <TidyScope state={{ anchor: { id: "t-tidy-1", title: "정리 — 2026-09-12", status: "in-progress", requestedAt: tidyAgo(3), running: true }, lastSweepAt: tidyAgo(180) }} openCount={42} onRequest={() => undefined} now={MOCK_NOW} />{/* copy-ignore: 견본 */}
          <TidyScope state={null} openCount={null} onRequest={() => undefined} busy now={MOCK_NOW} />
        </div>
        <h3>{tx("pat.tidy.result")}</h3>
        <TidyResult
          applied={2}
          skipped={[
            { proposalId: "tp-3", taskId: "t-in-sw-cache", skip: { code: "held", holder: "session", message: "누가 쥐고 있습니다 — 열린 세션(naru-1)이 이 이슈를 돌리는 중입니다. 세션이 끝난 뒤 다시 눌러 주세요." } }, // copy-ignore: 견본 자료
            { proposalId: "tp-2", taskId: "t-in-git-retry", skip: { code: "invalid transition" } },
            { proposalId: "tp-9", taskId: "t-in-x", skip: { code: "lease-expired-x" } },
          ]}
        />
        <TidyResult applied={3} skipped={[]} />
        <h3>{tx("pat.tidy.table")}</h3>
        <TidyTable
          rows={TIDY_ROWS}
          selected={new Set(["tp-1", "tp-2"])}
          onToggle={() => undefined}
          onToggleAll={() => undefined}
          onDismiss={() => undefined}
          onApply={() => undefined}
          onClear={() => undefined}
          now={MOCK_NOW}
        />
        <TidyLegend />
        <h3>{tx("pat.tidy.applying")}</h3>
        <TidyTable rows={TIDY_ROWS.slice(0, 2)} selected={new Set(["tp-1"])} busy onToggle={() => undefined} onToggleAll={() => undefined} onDismiss={() => undefined} onApply={() => undefined} onClear={() => undefined} now={MOCK_NOW} />
        <h3>{tx("pat.tidy.confirm")}</h3>
        <TidyTable rows={TIDY_LONG} selected={new Set(["tp-1", "tp-8"])} initialConfirm onToggle={() => undefined} onToggleAll={() => undefined} onDismiss={() => undefined} onApply={() => undefined} onClear={() => undefined} now={MOCK_NOW} />
        <h3>{tx("pat.tidy.states")}</h3>
        <div className="pat__grid">
          <TidyBody loading loadErr={null} onRetry={() => undefined} rows={[]} neverSwept={false} statusF="open" onRequest={() => undefined}>
            {null}
          </TidyBody>
          <TidyBody loading={false} loadErr="HTTP 503 — /api/tidy/proposals?status=all" onRetry={() => undefined} rows={[]} neverSwept={false} statusF="open" onRequest={() => undefined}>
            {null}
          </TidyBody>
          <TidyBody loading={false} loadErr={null} onRetry={() => undefined} rows={[]} neverSwept={false} statusF="open" onRequest={() => undefined}>
            {null}
          </TidyBody>
          <TidyBody loading={false} loadErr={null} onRetry={() => undefined} rows={[]} neverSwept statusF="open" onRequest={() => undefined}>
            {null}
          </TidyBody>
        </div>
      </Section>

      <Section
        id="alarm"
        title={tx("pat.nav.alarm")}
        lead={tx("pat.alarm.lead")}
      >
        <h3>{tx("pat.alarm.bell")}</h3>
        <div className="pat__row">
          <BellButton count={{ unread: 3, alert: 2 }} />
          <BellButton count={{ unread: 2, alert: 0 }} />
          <BellButton count={{ unread: 0, alert: 0 }} />
          <BellButton count={null} />
          <BellButton count="error" />
          <BellButton count={{ unread: 3, alert: 2 }} open />
        </div>
        <h3>{tx("pat.alarm.chips")}</h3>
        <div className="pat__row">
          {ALARMS.filter((a) => a.id !== "al-6").map((a) => (
            <AlarmKindChip key={a.id} a={a} />
          ))}
        </div>
        <h3>{tx("pat.alarm.popover")}</h3>
        <div className="pat__grid">
          <BellPanel inline state={{ kind: "ok", page: { items: ALARMS.slice(0, 6), nextBefore: "x", total: 41 } }} count={{ unread: 3, alert: 3 }} now={MOCK_NOW} />
          <BellPanel inline state={{ kind: "loading" }} count={{ unread: 3, alert: 3 }} now={MOCK_NOW} />
          <BellPanel inline state={{ kind: "ok", page: { items: [], nextBefore: null, total: 0 } }} count={{ unread: 0, alert: 0 }} now={MOCK_NOW} />
          <BellPanel inline state={{ kind: "error", msg: "HTTP 503 — /api/alarms?limit=30" }} count="error" now={MOCK_NOW} onRetry={() => undefined} />
        </div>
        <h3>{tx("pat.alarm.row")}</h3>
        <ul className="pat__list">
          <AlarmItem a={ALARMS[0]} now={MOCK_NOW} />
          <AlarmItem a={{ ...ALARMS[0], read: true }} now={MOCK_NOW} />
          <AlarmItem a={ALARMS.find((a) => a.id === "al-8")!} now={MOCK_NOW} />
          <AlarmItem a={ALARMS.find((a) => a.id === "al-9")!} now={MOCK_NOW} />
        </ul>
        <h3>{tx("pat.alarm.table")}</h3>
        <AlarmTable rows={ALARMS} selected={new Set(["al-1", "al-2"])} now={MOCK_NOW} onToggle={() => undefined} onToggleAll={() => undefined} onPick={() => undefined} onRead={() => undefined} onClear={() => undefined} />
        <AlarmTable rows={ALARMS.slice(0, 2)} selected={new Set()} now={MOCK_NOW} onToggle={() => undefined} onToggleAll={() => undefined} onPick={() => undefined} onRead={() => undefined} onClear={() => undefined} />
        <h3>{tx("pat.alarm.marking")}</h3>
        <AlarmTable rows={ALARMS.slice(0, 2)} selected={new Set(["al-1"])} now={MOCK_NOW} busy onToggle={() => undefined} onToggleAll={() => undefined} onPick={() => undefined} onRead={() => undefined} onClear={() => undefined} />
        <h3>{tx("pat.alarm.states")}</h3>
        <div className="pat__grid">
          <AlarmListState loading />
          <AlarmListState err="HTTP 503 — /api/alarms" onRetry={() => undefined} />
          <AlarmListState empty />
          <AlarmListState empty filtered />
        </div>
      </Section>

      <Section
        id="logs"
        title={tx("pat.nav.logs")}
        lead={tx("pat.logs.lead")}
      >
        <h3>{tx("pat.logs.summary")}</h3>
        <p className="caption">{rich(tx("pat.logs.chipsNote", hole("four")), { four: <b>{tx("pat.logs.chipsFour")}</b> })}</p>
        <div className="pat__stack">
          <div className="logs__summary">
            <span className="logs__count logs__count--error">
              <span aria-hidden="true">✕</span> {tx("logs.errors", { n: 3 })}
            </span>
            <span className="logs__count logs__count--warn">
              <span aria-hidden="true">△</span> {tx("logs.warns", { n: 12 })}
            </span>
            <span className="git__spacer" />
            {(["ok", "empty", "missing", "unreadable"] as ReadState[]).map((st) => (
              <span key={st} className={`logs__chip logs__chip--${st}`}>
                api · {LOG_STATE[st]}
              </span>
            ))}
          </div>
          <div className="logs__summary">
            <span className="logs__count">
              <span aria-hidden="true">✓</span> {tx("logs.errors", { n: 0 })}
            </span>
            <span className="logs__count">
              <span aria-hidden="true">△</span> {tx("logs.warns", { n: 0 })}
            </span>
            <span className="git__spacer" />
            <span className="logs__chip logs__chip--ok">api · {LOG_STATE.ok} {tx("chat.lines", { n: "1,204" })}</span>
          </div>
        </div>

        <h3>{tx("pat.logs.filters")}</h3>
        <div className="logs__filters">
          <div className="logs__seg" role="group" aria-label={tx("pat.logs.serviceSample")}>
            {[tx("logs.all"), "api", "mcp", "naru", "web"].map((s, i) => (
              <button key={s} type="button" className={`b-chip${i === 0 ? " is-on" : ""}`} aria-pressed={i === 0}>
                {s}
              </button>
            ))}
          </div>
          <div className="logs__seg" role="group" aria-label={tx("pat.logs.levelSample")}>
            {LOG_LEVELS.map((l, i) => (
              <button key={l.key} type="button" className={`b-chip${i === 1 ? " is-on" : ""}`} aria-pressed={i === 1}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <h3>{tx("pat.logs.groups")}</h3>
        <ul className="logs__list">
          <li className="logs__group logs__group--error">
            <span className="logs__glyph" aria-hidden="true">
              {LOG_GLYPH.error}
            </span>
            <span className="logs__level">ERROR</span>
            <span className="logs__service">api</span>
            <span className="logs__last">
              2026-09-18T09:00:01.000000Z ERROR api: db 에 못 붙었다 (시도 3){/* copy-ignore: 견본 */}
            </span>
            <span className="git__spacer" />
            <span className="logs__times">{tx("chat.stage.times", { n: 17 })}</span>
            <button type="button" className="b-btn b-btn-secondary b-btn-sm logs__ctxbtn">
              {tx("logs.ctxOpen")}
            </button>
          </li>
          <li className="logs__group logs__group--warn">
            <span className="logs__glyph" aria-hidden="true">
              {LOG_GLYPH.warn}
            </span>
            <span className="logs__level">WARN</span>
            <span className="logs__service">naru</span>
            <span className="logs__last">
              2026-09-18T09:00:04.000000Z WARN naru: 이 나루가 못 하는 일이 줄에 있다{/* copy-ignore: 견본 */}
            </span>
            <span className="git__spacer" />
            <span className="logs__times">{tx("chat.stage.times", { n: 2 })}</span>
            <button type="button" className="b-btn b-btn-secondary b-btn-sm logs__ctxbtn">
              {tx("logs.ctxOpen")}
            </button>
          </li>
        </ul>

        <h3>{tx("pat.logs.full")}</h3>
        <pre className="logs__pre">
          <div className="logs__line logs__line--info">
            <span aria-hidden="true">{LOG_GLYPH.info}</span> <span className="logs__service">api</span>{" "}
            2026-09-18T09:00:00.000000Z INFO api: 떴다{/* copy-ignore: 견본 */}
          </div>
          <div className="logs__line logs__line--error">
            <span aria-hidden="true">{LOG_GLYPH.error}</span> <span className="logs__service">api</span>{" "}
            2026-09-18T09:00:01.000000Z ERROR api: db 에 못 붙었다{/* copy-ignore: 견본 */}
          </div>
          <div className="logs__line logs__line--unknown">
            <span aria-hidden="true">{LOG_GLYPH.unknown}</span>{" "}
            <span className="logs__service">api</span> at crates/api/src/main.rs:42
          </div>
          <div className="logs__line logs__line--warn">
            <span aria-hidden="true">{LOG_GLYPH.warn}</span> <span className="logs__service">mcp</span>{" "}
            2026-09-18T09:00:02.000000Z WARN mcp: 세션이 Authorization 을 안 보냈다{/* copy-ignore: 견본 */}
          </div>
        </pre>
        <p className="caption">{rich(tx("pat.logs.unknownNote", hole("keep")), { keep: <b>{tx("pat.logs.keep")}</b> })}</p>

        <h3>{tx("pat.logs.ctx")}</h3>
        <pre className="logs__pre logs__pre--ctx">
          <div className="logs__line logs__line--info">
            <span aria-hidden="true">{LOG_GLYPH.info}</span> 2026-09-18T09:00:00.000000Z INFO api: db
            주소 postgres://… 로 붙는다{/* copy-ignore: 견본 */}
          </div>
          <div className="logs__line logs__line--error logs__line--at">
            <span aria-hidden="true">▸</span> 2026-09-18T09:00:01.000000Z ERROR api: db 에 못 붙었다{/* copy-ignore: 견본 */}
          </div>
          <div className="logs__line logs__line--unknown">
            <span aria-hidden="true">{LOG_GLYPH.unknown}</span> 2026-09-18T09:00:01.100000Z DEBUG api:
            재시도 대기 3s{/* copy-ignore: 견본 */}
          </div>
        </pre>
        <p className="caption">
          {rich(tx("pat.logs.ctxNote", hole("at", "that", "low")), {
            at: <code>--at</code>,
            that: <b>{tx("pat.logs.ctxThat")}</b>,
            low: <b>{tx("pat.logs.ctxLow")}</b>,
          })}
        </p>

        <h3>{tx("pat.logs.empties")}</h3>
        <div className="pat__stack">
          <EmptyState title={`✓ ${tx("logs.empty.quiet")}`} desc={tx("logs.empty.quietDesc")} />
          <EmptyState title={`○ ${tx("logs.empty.none")}`} desc={tx("pat.logs.noneDesc")} />
          <Alert tone="error">
            <span aria-hidden="true">✕</span>{" "}
            {rich(tx("pat.logs.unreadable", hole("head")), { head: <b>{tx("logs.someUnreadableHead")}</b> })}
          </Alert>
        </div>
      </Section>

      <Section
        id="auth"
        title={tx("pat.nav.auth")}
        lead={tx("pat.auth.lead")}
      >
        <h3>{tx("pat.auth.login")}</h3>
        <div className="pat__grid">
          <LoginCard />
          <LoginCard initialUser="admin" notice={tx("auth.sessionEnded")} />
          <LoginCard initialUser="admin" error="아이디 또는 비밀번호가 올바르지 않습니다." />{/* copy-ignore: 견본 */}
          <LoginCard initialUser="kim" error="이 계정은 막혀 있습니다. 관리자에게 문의해 주세요." />{/* copy-ignore: 견본 */}
          <LoginCard initialUser="admin" busy />
        </div>

        <h3>{tx("pat.auth.modes")}</h3>
        <div className="pat__grid">
          <LoginCard mode="sso" />
          <LoginCard mode="local+sso" />
          <LoginCard mode="local+sso" startOpen />
          <LoginCard mode="local+sso" ssoError="state" issuer="https://hub.mops.local" />
          <LoginCard mode="sso" ssoError="unreachable" issuer="https://hub.mops.local" />
          <LoginCard mode="sso" ssoError="token" issuer="https://hub.mops.local" />
        </div>

        <h3>{tx("pat.auth.setup")}</h3>
        <div className="pat__grid">
          <SetupCard />
          <SetupCard error="설치 토큰이 맞지 않습니다 — 서버에서 make auth-setup-token 으로 확인해 주세요." />{/* copy-ignore: 견본 */}
        </div>

        <h3>{tx("pat.auth.menu")}</h3>
        <div className="pat__row">
          <AccountMenu me={PAT_ME} />
          <AccountMenu me={{ ...PAT_ME, username: "kim", role: "user", displayName: "김민지" }} />{/* copy-ignore: 견본 */}
        </div>
        <div className="pat__grid">
          <AccountPanel inline me={PAT_ME} />
        </div>

        <h3>{tx("pat.auth.account")}</h3>
        <div className="pat__grid">
          <AccountInfo me={PAT_ME} />
          <AccountInfo me={{ ...PAT_ME, via: null, expiresAt: null }} />
          <PasswordCard hasPassword />
          <PasswordCard hasPassword done />
          <PasswordCard hasPassword error="비밀번호가 올바르지 않습니다." />{/* copy-ignore: 견본 */}
          <PasswordCard hasPassword={false} />
          <TokenWhereCard isAdmin />
          <TokenWhereCard isAdmin={false} />
        </div>

        <h3>{tx("pat.auth.users")}</h3>
        <UserTable
          rows={PAT_USERS}
          meId="u-admin"
          openId="u-park"
          projects={PAT_MCP_PROJECTS}
          error="마지막 관리자는 강등하거나 막을 수 없습니다 — 다른 관리자를 먼저 세워 주세요." /* copy-ignore: 서버 원문 견본 */
          extra={(u) => (u.totpEnabled ? <TotpAdminResetCell onReset={async () => undefined} /> : null)}
        />
        <h3>{tx("pat.auth.usersScope")}</h3>
        <UserTable rows={PAT_USERS} meId="u-admin" openId="u-kim" projects={PAT_MCP_PROJECTS} savedId="u-kim" />
        <UserTable rows={PAT_USERS} meId="u-admin" openId="u-admin" projects={PAT_MCP_PROJECTS} />
        <div className="pat__grid">
          <NewUserForm />
          <NewUserForm doneName="choi" />
          <NewUserForm error="같은 아이디가 이미 있습니다." />{/* copy-ignore: 견본 */}
        </div>
      </Section>

      <Section
        id="totp"
        title={tx("pat.nav.totp")}
        lead={tx("pat.totp.lead")}
      >
        <h3>{tx("pat.totp.login")}</h3>
        <div className="pat__grid">
          <TotpLoginCard onSubmit={patRejectCode} onRestart={() => undefined} />
          <TotpEnrollGate {...PAT_TOTP_FNS} title={tx("totp.enrollNeeded")} logoutLabel={tx("common.action.logout")} onLogout={() => undefined} />
        </div>
        <h3>{tx("pat.totp.account")}</h3>
        <div className="pat__grid">
          <TotpAccountCard enabled={false} required={false} fns={PAT_TOTP_FNS} />
          <TotpAccountCard enabled required={false} fns={PAT_TOTP_FNS} />
          <TotpAccountCard enabled required fns={PAT_TOTP_FNS} />
        </div>
        <h3>{tx("pat.totp.policy")}</h3>
        <div className="pat__grid">
          <TotpPolicyCard saved="off" onSave={async () => undefined} />
          <TotpPolicyCard saved="admin" onSave={async () => undefined} />
        </div>
      </Section>

      <Section id="runners" title={tx("pat.nav.runners")} lead={tx("pat.runners.lead")}>
        <h3>{tx("pat.runners.table")}</h3>
        <RunnerTable rows={PAT_RUNNERS} now={PAT_NOW} armedName="s205" />
        <h3>{tx("pat.runners.tableErr")}</h3>
        <RunnerTable
          rows={PAT_RUNNERS.slice(2, 3)}
          now={PAT_NOW}
          error="설정 파일(api 의 .env B3XIN_AGENT_TOKENS)에 적힌 러너는 웹에서 폐기할 수 없습니다 — 그 줄을 지우고 make up-api 로 api 를 다시 띄워 주세요." /* copy-ignore: 서버 원문 견본 */
        />
        <h3>{tx("pat.runners.empty")}</h3>
        <RunnerTable rows={[]} />
        <h3>{tx("pat.runners.form")}</h3>
        <div className="pat__grid">
          <NewRunnerForm />
          <NewRunnerForm error="이미 있는 이름입니다: s205 (설정 파일)" />{/* copy-ignore: 서버 원문 견본 */}
          <NewRunnerForm busy />
        </div>
        <h3>{tx("pat.runners.token")}</h3>
        <RunnerTokenCard name="s114" token="b3xr_3f9a0c1e7d2b4a6f8e1c3d5b7a9f0e2c4d6b8a1f3e5c7d9b0a2f4e6c8d1b3a5f" />
        <h3>{tx("pat.runners.pairBar")}</h3>
        <div className="rbar__tools">
          <PairCodeForm />
          <Button>{tx("runners.add")}</Button>
        </div>
        <h3>{tx("pat.runners.pairReq")}</h3>
        <div className="pat__grid">
          <PairRequestCard req={PAT_PAIR} now={PAT_NOW} />
          <PairRequestCard req={{ ...PAT_PAIR, nameTaken: "web" }} now={PAT_NOW} />
          <PairRequestCard
            req={{ ...PAT_PAIR, name: "s205", nameTaken: "env" }}
            now={PAT_NOW}
            error="이미 있는 이름입니다: s205 (설정 파일)" /* copy-ignore: 서버 원문 견본 */
          />
          <PairRequestCard req={PAT_PAIR} now={PAT_NOW} busy="approve" />
        </div>
        <h3>{tx("pat.runners.pairEnd")}</h3>
        <div className="pat__grid">
          <PairEndCard kind="approved" name="s114" />
          <PairEndCard kind="denied" />
          <PairEndCard kind="missing" />
        </div>
        <h3>{tx("pat.runners.mcpAsk")}</h3>
        <div className="pat__grid">
          <OAuthAskCard req={PAT_MCP} name="mcp-claude-code" onName={() => {}} busy={null} onApprove={() => {}} onDeny={() => {}} now={PAT_NOW} />
          <OAuthAskCard
            req={{ ...PAT_MCP, clientName: "Cursor", redirectUri: "https://cursor.example.com/oauth/cb" }}
            name="mcp-cursor"
            onName={() => {}}
            busy={null}
            onApprove={() => {}}
            onDeny={() => {}}
            now={PAT_NOW}
          />
        </div>
        <h3>{tx("pat.runners.mcpEnd")}</h3>
        <div className="pat__grid">
          <OAuthEndCard kind="approved" name="mcp-claude-code" />
          <OAuthEndCard kind="denied" />
          <OAuthEndCard kind="missing" />
        </div>
      </Section>

      <Section id="servers" title={tx("pat.nav.servers")} lead={tx("pat.servers.lead")}>
        <h3>{tx("pat.servers.mark")}</h3>
        <div className="pat__row">
          <ServerMark s={PAT_SERVER} />
          <ServerMark s={{ ...PAT_SERVER, connections: 0, downSince: "2026-10-25T11:46:00Z" }} now={PAT_NOW} />
          <ServerMark s={{ ...PAT_SERVER, connections: 0, downSince: "2026-10-23T09:00:00Z" }} now={PAT_NOW} />
          <ServerMark s={{ ...PAT_SERVER, connections: 0, downSince: null }} />
        </div>
        <h3>{tx("pat.servers.table")}</h3>
        <ServiceTable server={PAT_SERVER} />
        <h3>{tx("pat.servers.empty")}</h3>
        <div className="pat__grid">
          <ServiceTable server={{ ...PAT_SERVER, services: null }} />
          <ServiceTable server={{ ...PAT_SERVER, services: [] }} />
        </div>
        <h3>{tx("pat.servers.form")}</h3>
        <div className="pat__grid">
          <AddRepoForm host="114" used={["8100", "30443"]} suggestions={["irolab-co/mops-send"]} />
          <AddRepoForm host="114" used={["8100"]} error="서버 제어 대상이 틀렸습니다: invalid ops repo (HTTP 400)" />{/* copy-ignore: 서버 원문 견본 */}
          <AddRepoForm host="114" used={[]} busy />
        </div>
        <h3>{tx("pat.servers.jobs")}</h3>
        <AppendList now={PAT_NOW} tasks={[PAT_APPEND("t-a1", "ready", "mops-send"), PAT_APPEND("t-a2", "in-progress", "table-mops"), PAT_APPEND("t-a3", "blocked", "dbix-mops"), PAT_APPEND("t-a4", "done", "net-mops")]} />
      </Section>

      <Section id="mcpTokens" title={tx("pat.nav.mcpTokens")} lead={tx("pat.mcpTokens.lead")}>
        <h3>{tx("pat.mcpTokens.table")}</h3>
        <McpTokenTable rows={PAT_MCP_TOKENS} projects={PAT_MCP_PROJECTS} now={PAT_MCP_NOW} picked="codex-ci" />
        <h3>{tx("pat.mcpTokens.empty")}</h3>
        <McpTokenTable rows={[]} projects={PAT_MCP_PROJECTS} />
        <h3>{tx("pat.mcpTokens.form")}</h3>
        <div className="pat__grid">
          <NewMcpTokenForm projects={PAT_MCP_PROJECTS} />
          <NewMcpTokenForm projects={PAT_MCP_PROJECTS} error="만료는 무제한(null) · 7 · 30 · 90일 중에서 골라 주세요." />{/* copy-ignore: 서버 원문 견본 */}
        </div>
        <h3>{tx("pat.mcpTokens.edit")}</h3>
        <div className="pat__grid">
          <McpTokenEdit t={PAT_MCP_TOKENS[1]} projects={PAT_MCP_PROJECTS} />
          <McpTokenEdit t={PAT_MCP_TOKENS[2]} projects={PAT_MCP_PROJECTS} armed />
        </div>
        <h3>{tx("pat.mcpTokens.token")}</h3>
        <McpTokenCard name="claude-code-laptop" token="b3xr_7c1e9a3f5b0d2e4c6a8f1b3d5e7c9a0f2b4d6e8a1c3f5b7d9e0a2c4f6b8d1e3a" mcpUrl="https://b3x.example/mcp" />
      </Section>

      <Section id="chores" title={tx("pat.nav.chores")} lead={tx("pat.chores.lead")}>
        <h3>{tx("pat.chores.form")}</h3>
        <div className="pat__grid">
          <ChoreForm onSubmit={() => undefined} onCancel={() => undefined} />
          <ChoreForm initial={{ title: PAT_CHORES[0].title, body: "", link: "" }} error="같은 할 일이 이미 남아 있습니다 — 그 항목에 메모를 더해 주세요. (HTTP 409)" onSubmit={() => undefined} />{/* copy-ignore: 서버 원문 견본 */}
        </div>
        <h3>{tx("pat.chores.rows")}</h3>
        <div className="b-dt">
          {PAT_CHORES.map((c, i) => (
            <ChoreRow key={c.id} c={c} now={PAT_CHORE_NOW} editing={i === 2} onToggle={() => undefined} onEdit={() => undefined} onSave={() => undefined} onCancel={() => undefined} onDelete={() => undefined} />
          ))}
        </div>
        <h3>{tx("pat.chores.list")}</h3>
        <div className="pat__grid">
          <ChoreList rows={PAT_CHORES} showDone={false} onShowDone={() => undefined} row={(c) => <ChoreRow key={c.id} c={c} now={PAT_CHORE_NOW} onToggle={() => undefined} onEdit={() => undefined} onSave={() => undefined} onCancel={() => undefined} onDelete={() => undefined} />} />
          <ChoreList rows={[]} showDone={false} onShowDone={() => undefined} row={() => null} />
        </div>
      </Section>

      <Section id="orgs" title={tx("pat.nav.orgs")} lead={tx("pat.orgs.lead")}>
        <h3>{tx("pat.orgs.guide")}</h3>
        <RoleGuide />
        <h3>{tx("pat.orgs.cards")}</h3>
        <OrgGrid>
          {PAT_ORGS.map((o, i) => (
            <OrgCard key={o.id} o={o} isAdmin={i === 3} picked={i === 0} />
          ))}
        </OrgGrid>
        <h3>{tx("pat.orgs.new")}</h3>
        <div className="pat__grid">
          <NewOrgForm />
          <NewOrgForm error="같은 이름의 조직이 이미 있습니다. 다른 이름을 골라 주세요." />{/* copy-ignore: 서버 원문 견본 */}
        </div>
        <h3>{tx("pat.orgs.manageAdmin")}</h3>
        <OrgManage o={PAT_ORGS[0]} members={PAT_ORG_MEMBERS} isAdmin projects={PAT_ORG_PROJECTS} conns={PAT_ORG_CONNS} orgName={(id) => PAT_ORGS.find((o) => o.id === id)?.name ?? id} />
        <h3>{tx("pat.orgs.manageOwner")}</h3>
        <OrgManage o={PAT_ORGS[0]} members={PAT_ORG_MEMBERS} error="마지막 OWNER 는 빼거나 다른 역할로 바꿀 수 없습니다. 다른 사람을 먼저 OWNER 로 세워 주세요." />{/* copy-ignore: 서버 원문 견본 */}
        <h3>{tx("pat.orgs.manageRead")}</h3>
        <div className="pat__grid">
          <OrgManage o={PAT_ORGS[1]} members={PAT_ORG_MEMBERS.slice(0, 3)} />
          <OrgManage o={PAT_ORGS[2]} members={PAT_ORG_MEMBERS.slice(3)} />
        </div>
        <h3>{tx("pat.orgs.edge")}</h3>
        <div className="pat__grid">
          <OrgManage o={PAT_ORGS[3]} members={[]} isAdmin projects={PAT_ORG_PROJECTS} conns={PAT_ORG_CONNS} armed />
          <div>
            <MemberTable rows={[]} canEdit />
            <AddMemberForm busy />
          </div>
        </div>
      </Section>

      <Section id="usage" title={tx("pat.nav.usage")} lead={tx("pat.usage.lead")}>
        <UsageStates />
      </Section>
    </div>
    </AppShell>
    </TokenCtx.Provider>
  );
}

/** 이 화면의 우측 사이드바 — 갈래로 건너뛴다. */
export const PATTERN_SECTIONS = [
  { id: "type", label: tx("pat.nav.type") },
  { id: "color", label: tx("pat.nav.color") },
  { id: "space", label: tx("pat.nav.space") },
  { id: "spec", label: tx("pat.nav.spec") },
  { id: "states", label: tx("pat.nav.states") },
  { id: "buttons", label: tx("pat.states.button") },
  { id: "form", label: tx("pat.nav.form") },
  { id: "status", label: tx("pat.nav.status") },
  { id: "feedback", label: tx("pat.nav.feedback") },
  { id: "rail", label: tx("pat.nav.rail") },
  { id: "page", label: tx("pat.nav.page") },
  { id: "pwa", label: tx("pat.nav.pwa") },
  { id: "icons", label: tx("pat.nav.icons") },
  { id: "chat", label: tx("pat.nav.chat") },
  { id: "issue", label: tx("pat.nav.issue") },
  { id: "dash", label: tx("pat.nav.dash") },
  { id: "ms", label: tx("pat.nav.ms") },
  { id: "projects", label: tx("pat.nav.projects") },
  { id: "ops", label: tx("pat.nav.ops") },
  { id: "hist", label: tx("pat.nav.hist") },
  { id: "queue", label: tx("pat.nav.queue") },
  { id: "graph", label: tx("pat.nav.graph") },
  { id: "git", label: tx("pat.nav.git") },
  { id: "code", label: tx("pat.nav.code") },
  { id: "vendors", label: tx("pat.nav.vendors") },
  { id: "tidy", label: tx("pat.nav.tidy") },
  { id: "alarm", label: tx("pat.nav.alarm") },
  { id: "logs", label: tx("pat.nav.logs") },
  { id: "auth", label: tx("pat.nav.auth") },
  { id: "totp", label: tx("pat.nav.totp") },
  { id: "runners", label: tx("pat.nav.runners") },
  { id: "mcpTokens", label: tx("pat.nav.mcpTokens") },
  { id: "servers", label: tx("pat.nav.servers") },
  { id: "chores", label: tx("pat.nav.chores") },
  { id: "orgs", label: tx("pat.nav.orgs") },
  { id: "usage", label: tx("pat.nav.usage") },
];
